import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import autoAnimate from '@formkit/auto-animate';
import {
    IconAdjustmentsHorizontal,
    IconAlertTriangle,
    IconChecklist,
    IconChevronDown,
    IconChevronLeft,
    IconChevronRight,
    IconDeselect,
    IconDownload,
    IconFilterSearch,
    IconArrowLeft,
    IconArrowsShuffle,
    IconCircle,
    IconCircleCheck,
    IconDeviceFloppy,
    IconGripVertical,
    IconLayoutGrid,
    IconMinus,
    IconPencil,
    IconPhotoPlus,
    IconPlus,
    IconPrinter,
    IconLoader2,
    IconSearch,
    IconSelect,
    IconSquareCheck,
    IconThumbUp,
    IconThumbDown,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { notifications } from '@/lib/notifications';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import LimitReachedModal from '../../../components/common/LimitReachedModal';
import AiTemplateFeedbackModal from '../../../components/common/AiTemplateFeedbackModal';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useMediaQuery } from '../../../lib/hooks';
import { toBnDigits } from '../../../utils/bnFormat';
import { recordToolUsage } from '../../../services/toolsService';
import SelectField from '../../../components/common/SelectField';
import {
    DEFAULT_MARKS,
    QUESTION_TYPES,
    buildClassOptions,
    chaptersFor,
    questionImages,
    subjectsForClass,
} from './questionBankData';
import questionBankService from '../../../services/questionBankService';
import questionPaperService from '../../../services/questionPaperService';
import { generateQuestionPaperPdf } from './generateQuestionPaperPdf';
import { renderPaperIntoIframe } from './printQuestionPaper';
import { PAPER_TEMPLATES, DEFAULT_TEMPLATE_ID, getTemplate } from './paperTemplates';
import MathText from './MathText';

// Server caps the page at 200; we fetch that many and hint if more exist.
const RESULT_LIMIT = 200;

// A question paper may span at most this many A4 pages.
const MAX_PAGES = 10;

const inputClass =
    'w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800 placeholder:text-zinc-400 outline-none focus:ring-2 focus:ring-primary/40 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100';

const TYPE_ORDER = QUESTION_TYPES;

// Small styled-tooltip wrapper around an icon-only action button.
const Tip = ({ label, children }) => (
    <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
    </Tooltip>
);

// Skeleton primitive (pulsing placeholder block).
const Skel = ({ className = '' }) => (
    <div className={`animate-pulse rounded bg-zinc-100 dark:bg-zinc-800 ${className}`} />
);

// Paper-header logo. Tries a CORS-enabled load first (so html2canvas can paint
// it into the exported PDF); if the host doesn't send CORS headers the request
// fails, so we retry once WITHOUT crossOrigin — that keeps the logo visible on
// screen instead of vanishing. Only truly broken URLs end up hidden.
function PaperLogo({ src, style, centered }) {
    const [cors, setCors] = useState(true);
    useEffect(() => { setCors(true); }, [src]);
    return (
        <div className={`mb-1.5 flex ${centered ? 'justify-center' : 'justify-start'}`}>
            <img
                src={src}
                alt=""
                {...(cors ? { crossOrigin: 'anonymous' } : {})}
                onError={(e) => {
                    if (cors) setCors(false); // retry as a plain (non-CORS) load
                    else e.currentTarget.style.display = 'none';
                }}
                className="w-auto object-contain"
                style={style}
            />
        </div>
    );
}

// Bengali sub-question markers used in essay ("রচনামূলক") questions.
const BROAD_SUB_LETTERS = 'কখগঘঙচছজ';
// Matches a sub-question marker in either common style, at a word boundary
// (start / whitespace / danda): parenthesised "(ক)" OR letter+delimiter "ক." / "ক)".
const BROAD_MARKER_RE = new RegExp(
    `(?:^|[\\s।])(\\([${BROAD_SUB_LETTERS}]\\)|[${BROAD_SUB_LETTERS}][.)])`,
    'g',
);
// Split a broad-question string into its stem (the main passage) and its
// sub-parts ((ক)/(খ)/… or ক./খ./…), so each sub-question can render on its own
// line instead of running into one paragraph. Returns { stem, parts:[] }; parts
// is empty when the text has fewer than 2 markers (i.e. not a multi-part question).
function splitBroadQuestion(text) {
    if (!text) return { stem: text, parts: [] };
    const re = new RegExp(BROAD_MARKER_RE.source, 'g');
    const marks = [];
    let m;
    while ((m = re.exec(text)) !== null) {
        marks.push(m.index + m[0].indexOf(m[1])); // index where the marker itself starts
    }
    if (marks.length < 2) return { stem: text, parts: [] };
    const stem = text.slice(0, marks[0]).trim();
    const parts = marks.map((start, i) => text.slice(start, i + 1 < marks.length ? marks[i + 1] : text.length).trim());
    return { stem, parts };
}

export default function QuestionPaperEditor({ manual = false }) {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const num = (n) => (isBn ? toBnDigits(n) : String(n));
    const navigate = useNavigate();
    // Below the two-column breakpoint the sidebar is full-width, so a side="right"
    // tooltip has no horizontal room and spills off-screen — place it below instead.
    const isNarrow = useMediaQuery('(max-width: 1023px)');
    const tipSide = isNarrow ? 'bottom' : 'right';
    const { id: routeId } = useParams(); // present when editing a saved paper
    const [searchParams, setSearchParams] = useSearchParams();
    const getCurrentUser = useAuthStore((s) => s.getCurrentUser);
    const officeSpaceId = useOfficeSpaceStore((s) => s.selectedOfficeSpace?.id ?? s.selectedOfficeSpaceId ?? null);
    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const autoExportedRef = useRef(false); // guard so ?export=1 fires only once
    const previewRef = useRef(null);
    const measureRef = useRef(null);
    // Smoothly animate questions when they reflow (drag-reorder, add/remove).
    const animateRef = useCallback((el) => { if (el) autoAnimate(el, { duration: 160 }); }, []);

    const [cls, setCls] = useState('');
    const [subject, setSubject] = useState('');
    const [chapter, setChapter] = useState('');
    const [type, setType] = useState('');
    const [search, setSearch] = useState('');
    const [listSearch, setListSearch] = useState(''); // client-side search within fetched results
    const [activeTypes, setActiveTypes] = useState([]); // client-side type chips; empty = all
    const [selected, setSelected] = useState({}); // id -> question
    const [orderIds, setOrderIds] = useState([]); // custom question order (drag-drop)
    // ── Auto Create: randomly build a paper from multiple chapters ──
    const [browseMode, setBrowseMode] = useState('manual'); // 'manual' (browse & pick) | 'auto'
    const [autoChapters, setAutoChapters] = useState([]);   // multiple selected chapters
    const [autoTypes, setAutoTypes] = useState(['mcq', 'short', 'broad']); // included question types
    const [autoCount, setAutoCount] = useState('20');       // max total questions (locale digits)
    const [autoGenerating, setAutoGenerating] = useState(false);
    const [autoError, setAutoError] = useState('');
    const [dragId, setDragId] = useState(null);
    const [editingTextId, setEditingTextId] = useState(null); // question whose stem is being edited
    const [editingOpt, setEditingOpt] = useState(null); // { id, idx } option being edited
    const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
    const [printPreviewLoading, setPrintPreviewLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const printIframeRef = useRef(null);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [imgTick, setImgTick] = useState(0); // bumped when a preview image loads → re-measure pages

    const [institution, setInstitution] = useState(''); // school / coaching / college name (paper masthead)
    const [examTitle, setExamTitle] = useState('');
    const [examTime, setExamTime] = useState('');

    // ── Saved paper + template + layout ──────────────────────────────
    const [template, setTemplate] = useState(DEFAULT_TEMPLATE_ID);
    const tpl = getTemplate(template);
    const [paperId, setPaperId] = useState(routeId ? Number(routeId) : null);
    const [saving, setSaving] = useState(false);
    const [loadingPaper, setLoadingPaper] = useState(Boolean(routeId));
    // Like/dislike on the saved paper (shown after Save, mirrors the AI photo/template
    // feedback). One-shot: once rated, buttons give way to a "thanks" line. Seeded from
    // the loaded paper's stored feedback so an already-rated paper shows as rated.
    const [feedbackState, setFeedbackState] = useState(null); // 'positive' | 'negative' | null
    const [feedbackLoading, setFeedbackLoading] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [pageSize, setPageSize] = useState('a4'); // 'a4' | 'a5' | 'joint' (A4 landscape, two A5-size pages side by side)
    const [fontScale, setFontScale] = useState(1); // whole-paper font multiplier
    const [logo, setLogo] = useState(''); // paper header logo URL (R2)
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [setNoEnabled, setSetNoEnabled] = useState(false); // show a "Set No" on the paper
    const [setNo, setSetNo] = useState('');
    const [answerMode, setAnswerMode] = useState(false); // reveal the mark-correct controls
    const [footerText, setFooterText] = useState(''); // end-of-paper footer (empty ⇒ no footer rendered)
    const [headerNoteEnabled, setHeaderNoteEnabled] = useState(false); // show the instruction line at all
    const [headerNote, setHeaderNote] = useState(''); // optional instruction line under the paper header
    const [questionGap, setQuestionGap] = useState('normal'); // compact | normal | relaxed
    const [mcqColumns, setMcqColumns] = useState(1); // 1 or 2 columns for the MCQ section
    const [watermarkText, setWatermarkText] = useState(''); // faint diagonal watermark (empty ⇒ none)
    const [watermarkType, setWatermarkType] = useState('text'); // 'text' | 'image' — mutually exclusive
    const [watermarkImage, setWatermarkImage] = useState(''); // uploaded watermark image URL (R2)
    const [watermarkPos, setWatermarkPos] = useState('center'); // 9-anchor placement (see WM_POS)
    const [uploadingWatermark, setUploadingWatermark] = useState(false);
    const logoInputRef = useRef(null);
    const watermarkImgRef = useRef(null);
    // The paper fills the preview card width (A5 stays narrower to read as "small").
    // The on-screen width becomes the PDF render width, so export is WYSIWYG.
    const paperWidthStyle = pageSize === 'a5' ? { maxWidth: '66%' } : undefined;
    // Scale a base px font size by the whole-paper font multiplier.
    const fs = (px) => ({ fontSize: `${+(px * fontScale).toFixed(2)}px` });
    // Vertical gap between question blocks (user-adjustable spacing).
    const GAP_MB = { compact: 'mb-1', normal: 'mb-2.5', relaxed: 'mb-4' };
    const gapCls = GAP_MB[questionGap] || GAP_MB.normal;
    // Watermark anchor → flex alignment for the overlay layer (9-position grid).
    const WM_POS = {
        'top-left': 'items-start justify-start', 'top': 'items-start justify-center', 'top-right': 'items-start justify-end',
        'left': 'items-center justify-start', 'center': 'items-center justify-center', 'right': 'items-center justify-end',
        'bottom-left': 'items-end justify-start', 'bottom': 'items-end justify-center', 'bottom-right': 'items-end justify-end',
    };
    // Ordered anchors for the 3×3 position picker.
    const WM_POS_KEYS = ['top-left', 'top', 'top-right', 'left', 'center', 'right', 'bottom-left', 'bottom', 'bottom-right'];

    // ── Inline edits to selected questions (text / options / correct answer).
    // Mutating the stored question objects means edits persist in the saved paper.
    const editQuestion = (id, mutate) => setSelected((prev) => {
        const q = prev[id];
        if (!q) return prev;
        const next = { ...q };
        mutate(next);
        return { ...prev, [id]: next };
    });
    const updateQuestionText = (id, text) => editQuestion(id, (q) => { q.text = text; });
    const updateOption = (id, idx, text) => editQuestion(id, (q) => {
        const opts = [...(q.options || [])];
        opts[idx] = text;
        q.options = opts;
    });
    const toggleCorrect = (id, idx) => editQuestion(id, (q) => {
        q.correctAnswer = q.correctAnswer === idx ? null : idx;
    });

    // ── Marks ────────────────────────────────────────────────────────
    // The API returns marks=1 for every question, so marks are set locally per
    // paper: a default per section (bulk), overridable per question (inline).
    const [sectionMarks, setSectionMarks] = useState({ ...DEFAULT_MARKS });
    const [marksById, setMarksById] = useState({}); // per-question override: id -> marks
    const [editingMarkId, setEditingMarkId] = useState(null); // question whose mark is being edited inline
    const [markDraft, setMarkDraft] = useState(''); // in-progress inline mark text (may hold Bengali digits)

    // Effective marks for a question: per-question override → section default.
    const markOf = (q) => marksById[q.id] ?? sectionMarks[q.type] ?? q.marks;

    // Normalize Bengali digits back to Western so the value stays a real number.
    const bnToEn = (s) => String(s ?? '').replace(/[০-৯]/g, (d) => '০১২৩৪৫৬৭৮৯'.indexOf(d));

    // Commit an inline edit; clamp to a non-negative integer.
    const commitMark = (id, raw) => {
        setEditingMarkId(null);
        const n = Math.round(Number(bnToEn(raw)));
        if (!Number.isFinite(n) || n < 0) return;
        setMarksById((prev) => ({ ...prev, [id]: n }));
    };

    // Open the inline editor for a question, seeding the draft in the UI's locale.
    const startMarkEdit = (q) => { setMarkDraft(num(markOf(q))); setEditingMarkId(q.id); };

    // Keep only digits (Bengali or Western) and re-render them in the UI's locale.
    const onMarkDraftChange = (raw) => {
        const en = bnToEn(raw).replace(/[^0-9]/g, '');
        setMarkDraft(isBn ? toBnDigits(en) : en);
    };

    // ── Data from the Question Bank API ──────────────────────────────
    const [filterTree, setFilterTree] = useState({ classes: [] });
    const [results, setResults] = useState([]);
    const [resultsTotal, setResultsTotal] = useState(0);
    const [loadingResults, setLoadingResults] = useState(false);
    const [resultsError, setResultsError] = useState('');

    const typeLabel = (tp) => t(`questionsCreate.types.${tp}`);

    // Load the class → subject → chapter filter tree once.
    // Manual mode types questions directly — no question-bank API is touched.
    useEffect(() => {
        if (manual) return undefined;
        let cancelled = false;
        questionBankService
            .getFilters()
            .then((tree) => { if (!cancelled) setFilterTree(tree); })
            .catch(() => { if (!cancelled) setFilterTree({ classes: [] }); });
        return () => { cancelled = true; };
    }, []);

    // ── Filter option lists (dependent, derived from the tree) ───────
    // Prefix numeric classes with the "Class" label (e.g. "শ্রেণি ৮"); leave
    // non-numeric values like "SSC" untouched.
    const classPrefix = t('questionsCreate.paper.class');
    // Bengali labels for non-numeric exam levels (numeric classes get "শ্রেণি ৮" etc.).
    const LEVEL_BN = { SSC: 'এসএসসি', HSC: 'এইচএসসি' };
    const classOpts = useMemo(
        () => buildClassOptions(filterTree).map((c) => {
            const label = isBn ? c.labelBn : c.label;
            if (/^\d+$/.test(c.value)) return { value: c.value, label: `${classPrefix} ${label}` };
            return { value: c.value, label: isBn ? (LEVEL_BN[c.value] ?? c.value) : c.value };
        }),
        [filterTree, isBn, classPrefix],
    );
    const subjectOpts = useMemo(
        () => subjectsForClass(filterTree, cls).map((s) => ({ value: s, label: s })),
        [filterTree, cls],
    );
    const chapterOpts = useMemo(
        () => chaptersFor(filterTree, cls, subject).map((c) => ({ value: c, label: c })),
        [filterTree, cls, subject],
    );
    const typeOpts = [
        { value: '', label: t('questionsCreate.filters.allTypes') },
        ...TYPE_ORDER.map((tp) => ({ value: tp, label: typeLabel(tp) })),
    ];

    // Changing class/subject invalidates the current browse context. If the user
    // has already added questions to the paper, confirm before switching — on
    // confirm the selection is cleared from the paper; on cancel the filter
    // stays put (SelectField is controlled, so it reverts to the old value).
    const [pendingFilter, setPendingFilter] = useState(null); // { apply } while confirming
    const requestFilterChange = (apply) => {
        if (selectedList.length > 0) setPendingFilter({ apply });
        else apply();
    };
    const onClassChange = (v) => { if (v === cls) return; requestFilterChange(() => { setCls(v); setSubject(''); setChapter(''); setAutoChapters([]); }); };
    const onSubjectChange = (v) => { if (v === subject) return; requestFilterChange(() => { setSubject(v); setChapter(''); setAutoChapters([]); }); };
    const confirmFilterChange = () => { clearSelection(); pendingFilter?.apply?.(); setPendingFilter(null); };
    const cancelFilterChange = () => setPendingFilter(null);

    // The question-bank API returns a POSITIONAL `id` (1,2,3… reset per query), so
    // it collides across chapters/subjects — selecting id 1 in one chapter would
    // light up id 1 in the next. Derive a stable, content-based key instead and
    // use it as the question's identity everywhere (selection, order, marks, save).
    const stableId = (q) => ['q', q.class, q.subject, q.chapter, q.type, q.text].join('');
    // The bank also carries literal duplicate rows (same question imported twice),
    // which collapse to the same stable id — keep only the first, since duplicate
    // React keys corrupt list reconciliation (items duplicate/linger across
    // re-renders) and a paper never wants the same question twice anyway.
    const withStableId = (list) => {
        if (!Array.isArray(list)) return [];
        const seen = new Set();
        const out = [];
        for (const q of list) {
            const id = stableId(q);
            if (seen.has(id)) continue;
            seen.add(id);
            out.push({ ...q, id });
        }
        return out;
    };

    // ── Matching questions (fetched only when the user presses "Apply") ──
    const [hasApplied, setHasApplied] = useState(false);
    const requestIdRef = useRef(0);

    const applyFilters = async () => {
        if (!chapter) return; // a chapter must be selected before fetching
        const reqId = ++requestIdRef.current;
        setHasApplied(true);
        setLoadingResults(true);
        setResultsError('');
        setFiltersOpen(false); // collapse the filter panel so the list shows in its place
        setListSearch('');     // reset in-list refinements for the fresh result set
        setActiveTypes([]);
        try {
            const { items, pagination } = await questionBankService.getQuestions({
                class: cls, subject, chapter, type, search: search.trim(), page: 1, limit: RESULT_LIMIT,
            });
            if (reqId !== requestIdRef.current) return; // superseded by a newer apply
            setResults(withStableId(items));
            setResultsTotal(pagination?.total ?? items.length);
        } catch (err) {
            if (reqId !== requestIdRef.current) return;
            setResults([]);
            setResultsTotal(0);
            setResultsError(err?.message || t('questionsCreate.loadFailed'));
        } finally {
            if (reqId === requestIdRef.current) setLoadingResults(false);
        }
    };

    // ── Auto Create: fetch questions from every selected chapter, keep only the
    // chosen types, randomly sample up to `autoCount`, and REPLACE the paper. ──
    const generateAutoPaper = async () => {
        const max = Math.max(0, Math.round(Number(bnToEn(autoCount)) || 0));
        if (!subject || autoChapters.length === 0 || autoTypes.length === 0 || max === 0) return;
        setAutoGenerating(true);
        setAutoError('');
        try {
            const batches = await Promise.all(
                autoChapters.map((ch) =>
                    questionBankService
                        .getQuestions({ class: cls, subject, chapter: ch, page: 1, limit: RESULT_LIMIT })
                        .then((r) => r.items || [])
                        .catch(() => []),
                ),
            );
            // Pool → stable id → keep chosen types → dedupe by id.
            const seen = new Set();
            const pool = withStableId(batches.flat()).filter(
                (q) => autoTypes.includes(q.type) && (seen.has(q.id) ? false : (seen.add(q.id), true)),
            );
            if (pool.length === 0) { setAutoError(t('questionsCreate.auto.noQuestions')); return; }
            // Fisher–Yates shuffle, then take up to `max`.
            for (let i = pool.length - 1; i > 0; i -= 1) {
                const j = Math.floor(Math.random() * (i + 1));
                [pool[i], pool[j]] = [pool[j], pool[i]];
            }
            const picked = pool.slice(0, max);
            const nextSelected = {};
            const nextOrder = [];
            picked.forEach((q) => { nextSelected[q.id] = q; nextOrder.push(q.id); });
            setSelected(nextSelected);
            setOrderIds(nextOrder);
            notifications.show({ message: t('questionsCreate.auto.generated', { count: num(picked.length) }), color: 'green' });
        } catch (err) {
            setAutoError(err?.message || t('questionsCreate.loadFailed'));
        } finally {
            setAutoGenerating(false);
        }
    };

    // Selected questions in the user's custom order (drag-drop reorderable).
    const selectedList = useMemo(
        () => orderIds.map((id) => selected[id]).filter(Boolean),
        [orderIds, selected],
    );

    const totalMarks = selectedList.reduce((sum, q) => sum + markOf(q), 0);

    const toggle = (q) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[q.id]) delete next[q.id]; else next[q.id] = q;
            return next;
        });
        setOrderIds((prev) => (prev.includes(q.id) ? prev.filter((id) => id !== q.id) : [...prev, q.id]));
    };

    // ── Client-side refinement of the fetched results (search + type chips) ──
    // Counts per type across the full fetched set — drives the chip labels.
    const typeCounts = useMemo(() => {
        const c = { mcq: 0, short: 0, broad: 0 };
        results.forEach((q) => { c[q.type] = (c[q.type] || 0) + 1; });
        return c;
    }, [results]);

    // The fetched results narrowed by the in-list search box and active type chips.
    const visibleResults = useMemo(() => {
        const needle = listSearch.trim().toLowerCase();
        return results.filter((q) => {
            if (activeTypes.length && !activeTypes.includes(q.type)) return false;
            if (needle && !q.text.toLowerCase().includes(needle)) return false;
            return true;
        });
    }, [results, listSearch, activeTypes]);

    const isRefining = listSearch.trim() !== '' || activeTypes.length > 0;

    // Single-select: a chip replaces the previous one; clicking it again clears
    // back to "All". (State stays an array so the filter logic is untouched.)
    const toggleActiveType = (tp) =>
        setActiveTypes((prev) => (prev.includes(tp) ? [] : [tp]));

    // True when every currently-visible result is already selected.
    const allVisibleSelected = visibleResults.length > 0 && visibleResults.every((q) => selected[q.id]);

    // Toggle: select all visible, or (if all already selected) deselect them.
    const toggleSelectAllVisible = () => {
        const visibleIds = visibleResults.map((q) => q.id);
        if (allVisibleSelected) {
            const visibleSet = new Set(visibleIds);
            setSelected((prev) => {
                const next = { ...prev };
                visibleSet.forEach((id) => delete next[id]);
                return next;
            });
            setOrderIds((prev) => prev.filter((id) => !visibleSet.has(id)));
        } else {
            setSelected((prev) => {
                const next = { ...prev };
                visibleResults.forEach((q) => { next[q.id] = q; });
                return next;
            });
            setOrderIds((prev) => {
                const add = visibleIds.filter((id) => !prev.includes(id));
                return [...prev, ...add];
            });
        }
    };

    const clearSelection = () => { setSelected({}); setOrderIds([]); };

    // ── Manual question entry (manual mode) ──────────────────────────
    // Questions are typed directly into the paper — same shape as bank
    // questions, so the preview / pagination / marks / save / export are
    // all reused unchanged.
    const manualIdRef = useRef(0);
    const [manualType, setManualType] = useState('mcq');
    const [manualText, setManualText] = useState('');
    const [manualOptions, setManualOptions] = useState(['', '', '', '']);
    const [manualCorrect, setManualCorrect] = useState(null); // option index, or null
    const [manualMarks, setManualMarks] = useState(''); // optional per-question override (locale digits)
    // Srijonshil (creative/board) sub-questions ক/খ/গ/ঘ — broad type only.
    const [manualParts, setManualParts] = useState(['', '', '', '']);

    const manualOptionCount = manualOptions.filter((o) => o.trim()).length;
    const manualCanAdd = manualText.trim() !== '' && (manualType !== 'mcq' || manualOptionCount >= 2);

    const addManualQuestion = () => {
        const stem = manualText.trim();
        if (!stem) return;
        const isMcq = manualType === 'mcq';
        const isBroad = manualType === 'broad';
        const opts = isMcq ? manualOptions.map((o) => o.trim()).filter(Boolean) : [];
        if (isMcq && opts.length < 2) return;

        // Srijonshil (creative) question: stimulus/passage + ক/খ/গ/ঘ sub-questions,
        // composed into the one string that splitBroadQuestion() renders line-by-line.
        let text = stem;
        if (isBroad) {
            const parts = manualParts
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p, i) => `${BROAD_SUB_LETTERS[i]}. ${p}`);
            text = [stem, ...parts].join('\n');
        }

        const id = `man-${Date.now().toString(36)}-${(manualIdRef.current += 1)}`;
        const q = {
            id,
            type: manualType,
            text,
            options: opts,
            correctAnswer: isMcq && manualCorrect != null && manualCorrect < opts.length ? manualCorrect : null,
            marks: sectionMarks[manualType] ?? DEFAULT_MARKS[manualType],
            class: '', subject: '', chapter: '',
        };
        setSelected((prev) => ({ ...prev, [id]: q }));
        setOrderIds((prev) => [...prev, id]);
        const mk = Math.round(Number(bnToEn(manualMarks)));
        if (manualMarks !== '' && Number.isFinite(mk) && mk >= 0) {
            setMarksById((prev) => ({ ...prev, [id]: mk }));
        }
        // Reset for the next entry (keep the chosen type for fast repeat entry).
        setManualText('');
        setManualOptions(['', '', '', '']);
        setManualParts(['', '', '', '']);
        setManualCorrect(null);
        setManualMarks('');
    };

    // Randomise the order of the selected questions (within each type group, since
    // grouped sections bucket by type) so the same paper can be exported as a
    // different "set" — a fresh question order for each Set No.
    const shuffleQuestions = () => {
        setOrderIds((prev) => {
            if (prev.length < 2) return prev;
            const arr = [...prev];
            for (let i = arr.length - 1; i > 0; i -= 1) {
                const j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
            return arr;
        });
    };

    // Live reorder — called continuously while dragging OVER a target so the
    // questions physically shuffle to their new position in real time. Only
    // questions of the same type may swap, so the grouped sections stay intact.
    const moveDragTo = (targetId, edge) => {
        if (!dragId || dragId === targetId) return;
        if (selected[dragId]?.type !== selected[targetId]?.type) return;
        setOrderIds((prev) => {
            const from = prev.indexOf(dragId);
            if (from === -1) return prev;
            const next = [...prev];
            next.splice(from, 1);
            let to = next.indexOf(targetId);
            if (edge === 'after') to += 1;
            next.splice(to, 0, dragId);
            if (next.every((id, i) => id === prev[i])) return prev; // no change → no re-render
            return next;
        });
    };

    // ── Grouped questions for the preview paper ──────────────────────
    const grouped = useMemo(() => {
        const g = { mcq: [], short: [], broad: [] };
        selectedList.forEach((q) => g[q.type].push(q));
        return TYPE_ORDER.map((tp) => ({ type: tp, items: g[tp] })).filter((s) => s.items.length);
    }, [selectedList]);

    const optionLetters = isBn ? ['ক', 'খ', 'গ', 'ঘ'] : ['a', 'b', 'c', 'd'];

    // ── Flatten the paper into blocks (section headers + numbered questions) ──
    // so the preview can be measured and split across A4 pages.
    const blocks = useMemo(() => {
        const arr = [];
        let n = 0;
        grouped.forEach((sec) => {
            arr.push({
                key: `sec-${sec.type}`,
                kind: 'section',
                type: sec.type,
                marks: sec.items.reduce((s, q) => s + markOf(q), 0),
            });
            // MCQ section in 2-column mode: pair consecutive questions into a
            // single row block so each row is measured/paginated as one unit.
            if (sec.type === 'mcq' && mcqColumns === 2) {
                let pair = [];
                sec.items.forEach((q) => {
                    n += 1;
                    pair.push({ q, n });
                    if (pair.length === 2) { arr.push({ key: `row-${pair[0].q.id}`, kind: 'qrow', items: pair }); pair = []; }
                });
                if (pair.length) arr.push({ key: `row-${pair[0].q.id}`, kind: 'qrow', items: pair });
            } else {
                sec.items.forEach((q) => { n += 1; arr.push({ key: q.id, kind: 'q', q, n }); });
            }
        });
        // End-of-paper footer (e.g. "সমাপ্ত"), placed after the last question so it
        // is measured + paginated onto the final page. Only rendered when the user
        // actually typed a footer — an empty input shows no footer (and no border).
        if (arr.length && footerText.trim()) arr.push({ key: 'paper-footer', kind: 'footer' });
        return arr;
    }, [grouped, marksById, sectionMarks, mcqColumns, footerText]);

    // Renders one paper block. Used in the visible sheet (draggable) and in the
    // hidden measuring container (static) with identical markup so heights match.
    const renderBlock = (block, { draggable, measure } = {}) => {
        const measureAttr = measure ? { 'data-measure-block': '' } : {};
        if (block.kind === 'footer') {
            return (
                <p key={block.key} {...measureAttr} className={tpl.footer} style={fs(12.5)}>
                    {footerText.trim()}
                </p>
            );
        }
        if (block.kind === 'section') {
            return (
                <p key={block.key} {...measureAttr} className={tpl.section} style={fs(13)}>
                    {typeLabel(block.type)}
                    <span className="ml-2 font-normal text-zinc-500">
                        ({t('questionsCreate.markUnit', { count: num(block.marks) })})
                    </span>
                </p>
            );
        }
        // Renders a single question's inner markup. Reused for a normal 1-up
        // block and for each cell of a 2-up MCQ row.
        const renderQuestionInner = (q, n) => {
        const isDragging = dragId === q.id;
        const editingThisBlock = editingTextId === q.id || editingOpt?.id === q.id;
        const inner = (
            <div
                key={q.id}
                draggable={draggable && !editingThisBlock ? true : undefined}
                onDragStart={draggable ? (e) => { setDragId(q.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
                onDragOver={draggable ? (e) => {
                    // Live-reorder: shuffle the dragged question to this position as
                    // the pointer moves over it, so movement is shown in real time.
                    e.preventDefault();
                    if (!dragId || dragId === q.id || selected[dragId]?.type !== q.type) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const edge = (e.clientY - rect.top) < rect.height / 2 ? 'before' : 'after';
                    moveDragTo(q.id, edge);
                } : undefined}
                onDrop={draggable ? (e) => { e.preventDefault(); setDragId(null); } : undefined}
                onDragEnd={draggable ? () => setDragId(null) : undefined}
                style={fs(13)}
                className={`group/q relative flex gap-1.5 rounded leading-relaxed transition-colors ${draggable ? 'cursor-move' : ''} ${isDragging ? 'cursor-grabbing opacity-40' : ''}`}
            >
                {/* left: drag grip (screen-only) */}
                {draggable && (
                    <span
                        data-dnd-handle
                        title={t('questionsCreate.dragHint')}
                        className="flex-none cursor-grab self-start pt-0.5 text-zinc-300 opacity-0 transition-opacity group-hover/q:opacity-100 active:cursor-grabbing"
                    >
                        <IconGripVertical size={14} />
                    </span>
                )}

                <span className="flex-none font-semibold">{num(n)}.</span>

                <span className="min-w-0 flex-1">
                    {/* question stem — click to edit raw text (incl. LaTeX) */}
                    {draggable && editingTextId === q.id ? (
                        <textarea
                            data-dnd-handle
                            autoFocus
                            rows={2}
                            defaultValue={q.text}
                            draggable={false}
                            onFocus={(e) => e.currentTarget.select()}
                            onBlur={(e) => { updateQuestionText(q.id, e.target.value); setEditingTextId(null); }}
                            onKeyDown={(e) => { if (e.key === 'Escape') setEditingTextId(null); }}
                            style={fs(13)}
                            className="w-full resize-y rounded border border-primary/50 bg-white px-1.5 py-1 leading-relaxed text-zinc-800 outline-none focus:ring-1 focus:ring-primary/40"
                        />
                    ) : (
                        <span
                            onClick={draggable ? () => setEditingTextId(q.id) : undefined}
                            title={draggable ? t('questionsCreate.editTextHint') : undefined}
                            className={draggable ? 'cursor-text rounded transition-colors hover:bg-primary/5' : ''}
                        >
                            {(() => {
                                // Essay questions: keep the passage as one paragraph, then
                                // drop each sub-question (ক./খ./গ.…) onto its own line.
                                const broad = q.type === 'broad' ? splitBroadQuestion(q.text) : null;
                                if (broad && broad.parts.length) {
                                    return (
                                        <>
                                            {broad.stem && <MathText text={broad.stem} />}
                                            <span className="mt-1 block pl-1">
                                                {broad.parts.map((p, i) => (
                                                    <span key={i} className="block leading-relaxed">
                                                        <MathText text={p} />
                                                    </span>
                                                ))}
                                            </span>
                                        </>
                                    );
                                }
                                return <MathText text={q.text} />;
                            })()}
                        </span>
                    )}

                    {q.options?.length > 0 && (
                        <span className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 pl-1 text-zinc-700" style={fs(12.5)}>
                            {q.options.map((opt, i) => {
                                // Correct option: manual override wins, else the API's `answer` index.
                                const correctIdx = q.correctAnswer ?? (Number.isInteger(q.answer) ? q.answer : null);
                                const isCorrect = correctIdx === i;
                                const isEditingThis = editingOpt?.id === q.id && editingOpt?.idx === i;
                                return (
                                    <span key={i} className={`flex items-start gap-1 ${answerMode && isCorrect ? 'font-semibold text-primary' : ''}`}>
                                        {draggable && answerMode && (
                                            <Tip label={t('questionsCreate.markCorrect')}>
                                                <button
                                                    type="button"
                                                    data-dnd-handle
                                                    draggable={false}
                                                    onClick={() => toggleCorrect(q.id, i)}
                                                    aria-label={t('questionsCreate.markCorrect')}
                                                    className={`mt-[3px] flex-none ${isCorrect ? 'text-primary' : 'text-zinc-300 hover:text-primary'}`}
                                                >
                                                    {isCorrect ? <IconCircleCheck size={13} /> : <IconCircle size={13} />}
                                                </button>
                                            </Tip>
                                        )}
                                        <span className="mt-[1px] flex-none inline-flex h-[1.5em] w-[1.5em] items-center justify-center rounded-full border border-zinc-500 leading-none">{optionLetters[i]}</span>
                                        {isEditingThis ? (
                                            <input
                                                data-dnd-handle
                                                autoFocus
                                                defaultValue={opt}
                                                draggable={false}
                                                onFocus={(e) => e.currentTarget.select()}
                                                onBlur={(e) => { updateOption(q.id, i, e.target.value); setEditingOpt(null); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); else if (e.key === 'Escape') setEditingOpt(null); }}
                                                style={fs(12.5)}
                                                className="min-w-0 flex-1 rounded border border-primary/50 bg-white px-1 py-0.5 text-zinc-800 outline-none focus:ring-1 focus:ring-primary/40"
                                            />
                                        ) : (
                                            <span className="min-w-0 flex-1">
                                                {opt !== '' && (
                                                    <span
                                                        onClick={draggable ? () => setEditingOpt({ id: q.id, idx: i }) : undefined}
                                                        className={`min-w-0 ${draggable ? 'cursor-text rounded hover:bg-primary/5' : ''}`}
                                                    >
                                                        <MathText text={opt} />
                                                    </span>
                                                )}
                                                {/* Figure options — graphs/diagrams stored as images instead of text */}
                                                {(q.optionImages?.[i] ?? []).length > 0 && (
                                                    <span className="mt-0.5 flex flex-wrap gap-1.5">
                                                        {q.optionImages[i].map((src, j) => (
                                                            <img
                                                                key={j}
                                                                src={src}
                                                                alt=""
                                                                loading="lazy"
                                                                onLoad={measure ? () => setImgTick((n) => n + 1) : undefined}
                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                className="max-h-28 w-auto rounded border border-zinc-200 object-contain"
                                                            />
                                                        ))}
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </span>
                                );
                            })}
                        </span>
                    )}

                    {questionImages(q).length > 0 && (
                        <span className="mt-2 flex flex-wrap gap-3">
                            {questionImages(q).map((src, i) => (
                                <img
                                    key={i}
                                    src={src}
                                    alt=""
                                    loading="lazy"
                                    onLoad={measure ? () => setImgTick((n) => n + 1) : undefined}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    className="max-h-52 w-auto rounded border border-zinc-300 object-contain"
                                />
                            ))}
                        </span>
                    )}
                </span>

                {/* marks — inline editable */}
                {draggable && editingMarkId === q.id ? (
                    <input
                        data-mark-input
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        draggable={false}
                        value={markDraft}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => onMarkDraftChange(e.target.value)}
                        onBlur={() => commitMark(q.id, markDraft)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            else if (e.key === 'Escape') setEditingMarkId(null);
                        }}
                        onDragStart={(e) => e.stopPropagation()}
                        style={fs(12)}
                        className="h-5 w-6 flex-none rounded border border-primary/60 bg-white px-0 text-center leading-none text-zinc-700 outline-none focus:ring-1 focus:ring-primary/40"
                    />
                ) : draggable ? (
                    <button
                        type="button"
                        draggable={false}
                        onClick={() => startMarkEdit(q)}
                        onDragStart={(e) => e.stopPropagation()}
                        title={t('questionsCreate.editMarkHint')}
                        style={fs(12)}
                        className="h-5 w-6 flex-none rounded text-center leading-5 text-zinc-500 transition-colors hover:bg-primary/10 hover:text-primary"
                    >
                        {num(markOf(q))}
                    </button>
                ) : (
                    <span className="flex-none text-zinc-500" style={fs(12)}>{num(markOf(q))}</span>
                )}

                {/* right: remove (screen-only, darker) */}
                {draggable && (
                    <button
                        type="button"
                        data-dnd-handle
                        draggable={false}
                        onClick={() => toggle(q)}
                        onDragStart={(e) => e.stopPropagation()}
                        title={t('questionsCreate.removeQuestion')}
                        aria-label={t('questionsCreate.removeQuestion')}
                        className="flex-none self-start pt-0.5 text-zinc-400 opacity-0 transition-colors hover:text-red-500 group-hover/q:opacity-100"
                    >
                        <IconX size={15} />
                    </button>
                )}
            </div>
        );
        // Measuring container: no interactive tooltip (kept off-screen).
        if (!draggable) return inner;
        // Preview: hover shows the same styled details card as the sidebar list.
        return (
            <Tooltip key={q.id}>
                <TooltipTrigger asChild>{inner}</TooltipTrigger>
                <TooltipContent side={tipSide} align="start" collisionPadding={12} className="max-w-[min(260px,92vw)] font-hind">
                    <div className="space-y-1 text-[11.5px] leading-relaxed">
                        <div className="font-semibold"><MathText text={q.text} /></div>
                        {q.subject && <p><span className="opacity-60">{t('questionsCreate.paper.subject')}:</span> {q.subject}</p>}
                        {q.chapter && <p><span className="opacity-60">{t('questionsCreate.chapterLabel')}:</span> {q.chapter}</p>}
                        <p className="flex flex-wrap gap-x-3">
                            <span><span className="opacity-60">{t('questionsCreate.questionType')}:</span> {typeLabel(q.type)}</span>
                            <span>{t('questionsCreate.markUnit', { count: num(markOf(q)) })}</span>
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        );
        };
        // 2-up MCQ row: two questions side by side, measured/paginated as one block.
        if (block.kind === 'qrow') {
            return (
                <div key={block.key} {...measureAttr} className={`${gapCls} grid grid-cols-2 items-start gap-x-6`}>
                    {block.items.map((it) => renderQuestionInner(it.q, it.n))}
                </div>
            );
        }
        // Normal single question block.
        return (
            <div key={block.key} {...measureAttr} className={gapCls}>
                {renderQuestionInner(block.q, block.n)}
            </div>
        );
    };

    // Paper header (rendered on the first page only). In `editable` mode the
    // title and time are edited inline directly on the paper; the static mode
    // (used for measuring + PDF export) renders them as plain text.
    const classText = cls ? (isBn ? (LEVEL_BN[cls] ?? toBnDigits(cls)) : cls) : '';
    const h = tpl.header;
    const timeEl = (editable) =>
        (editable ? (
            <span data-export-optional className="inline-flex items-center gap-1">
                {t('questionsCreate.paper.time')}:
                <input
                    data-export-text
                    value={examTime}
                    onChange={(e) => setExamTime(e.target.value)}
                    placeholder={t('questionsCreate.paper.timePlaceholder')}
                    className="w-28 rounded bg-transparent text-left outline-none transition-colors placeholder:text-zinc-300 hover:bg-primary/5 focus:bg-primary/5"
                />
            </span>
        ) : examTime.trim() ? (
            <span>{t('questionsCreate.paper.time')}: {examTime.trim()}</span>
        ) : (
            <span />
        ));
    const paperHeader = (editable) => (
        <div className={h.wrap}>
            {/* Set number (top-right, like a real exam paper) */}
            {setNoEnabled && (
                <p className="mb-0.5 text-right font-semibold text-zinc-800" style={fs(12.5)}>
                    {t('questionsCreate.paper.setNo')}: {setNo.trim() || '—'}
                </p>
            )}
            {/* Logo (paper header) */}
            {logo && (
                <PaperLogo
                    src={logo}
                    centered={h.metaStacked}
                    style={{ maxHeight: `${Math.round(56 * fontScale)}px` }}
                />
            )}
            {/* Institution name (school / coaching / college) */}
            {editable ? (
                <input
                    data-export-text
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    placeholder={t('questionsCreate.paper.institutionPlaceholder')}
                    title={t('questionsCreate.paper.institutionHint')}
                    style={{ ...fs(h.titlePx + 3), lineHeight: 1.4 }}
                    className={`${h.title} w-full rounded bg-transparent py-0.5 font-extrabold outline-none transition-colors placeholder:font-normal placeholder:normal-case placeholder:tracking-normal placeholder:text-zinc-300 hover:bg-primary/5 focus:bg-primary/5`}
                />
            ) : (
                institution.trim() && (
                    <h1 className={`${h.title} font-extrabold`} style={{ ...fs(h.titlePx + 3), lineHeight: 1.4 }}>
                        {institution.trim()}
                    </h1>
                )
            )}
            {/* Exam title */}
            {editable ? (
                <input
                    data-export-text
                    data-export-fallback={t('questionsCreate.paper.defaultTitle')}
                    value={examTitle}
                    onChange={(e) => setExamTitle(e.target.value)}
                    placeholder={t('questionsCreate.paper.titlePlaceholder')}
                    title={t('questionsCreate.paper.editTitleHint')}
                    style={{ ...fs(h.titlePx), lineHeight: 1.5 }}
                    className={`${h.title} w-full rounded bg-transparent py-0.5 outline-none transition-colors placeholder:font-normal placeholder:normal-case placeholder:tracking-normal placeholder:text-zinc-300 hover:bg-primary/5 focus:bg-primary/5`}
                />
            ) : (
                <h2 className={h.title} style={{ ...fs(h.titlePx), lineHeight: 1.5 }}>
                    {examTitle.trim() || t('questionsCreate.paper.defaultTitle')}
                </h2>
            )}

            {h.metaStacked ? (
                <>
                    {subject && (
                        <p className={h.subject} style={fs(13.5)}>{t('questionsCreate.paper.subject')}: {subject}</p>
                    )}
                    {cls && (
                        <p className={h.klass} style={fs(13)}>{t('questionsCreate.paper.class')}: {classText}</p>
                    )}
                    <div className={h.row} style={fs(12.5)}>
                        {timeEl(editable)}
                        <span>{t('questionsCreate.paper.fullMarks')}: {num(totalMarks)}</span>
                    </div>
                </>
            ) : (
                /* Inline meta: class · subject · time · marks all on one row */
                <div className={h.row} style={fs(12.5)}>
                    {cls && <span>{t('questionsCreate.paper.class')}: {classText}</span>}
                    {subject && <span>{t('questionsCreate.paper.subject')}: {subject}</span>}
                    {timeEl(editable)}
                    <span>{t('questionsCreate.paper.fullMarks')}: {num(totalMarks)}</span>
                </div>
            )}

            {/* Optional instruction / note line under all header content — only
                rendered when enabled, so nothing (and no gap) shows when off.
                In editable mode the input sits inside a data-export-optional
                wrapper carrying the template note styling, so a blank note (and
                any template rule/border) is dropped entirely on export. */}
            {headerNoteEnabled && (editable ? (
                <div data-export-optional className={h.note} style={fs(12)}>
                    <input
                        data-export-text
                        value={headerNote}
                        onChange={(e) => setHeaderNote(e.target.value)}
                        placeholder={t('questionsCreate.paper.headerNotePlaceholder')}
                        title={t('questionsCreate.paper.headerNoteHint')}
                        className="w-full rounded bg-transparent outline-none transition-colors placeholder:font-normal placeholder:text-zinc-300 hover:bg-primary/5 focus:bg-primary/5"
                        style={{ textAlign: 'inherit', fontStyle: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
                    />
                </div>
            ) : (
                headerNote.trim() && (
                    <p className={h.note} style={fs(12)}>{headerNote.trim()}</p>
                )
            ))}
        </div>
    );

    // ── Split blocks into A4 pages by measuring their rendered heights ──
    const [pages, setPages] = useState([]);
    const [activePage, setActivePage] = useState(0);
    // FIXED logical paper width used for rendering, measuring, pagination AND the
    // PDF — so page breaks are identical on every screen (a narrow phone no longer
    // shrinks the page height and splits the paper into extra pages). The visible
    // paper is scaled with CSS `zoom` to fit the available width (see paperScale).
    // Joint mode paginates content into A5-width columns (each half of an A4
    // landscape sheet IS an A5 page), then pairs two columns per printed sheet.
    const PAPER_W = pageSize === 'a4' ? 720 : 512;
    const isJoint = pageSize === 'joint';
    const SHEET_W = isJoint ? PAPER_W * 2 : PAPER_W;
    const paperScrollRef = useRef(null);
    const [containerW, setContainerW] = useState(PAPER_W);
    const paperScale = Math.min(1, containerW / SHEET_W);

    useLayoutEffect(() => {
        const el = paperScrollRef.current;
        if (!el) return undefined;
        // Only ever accept a real, positive width. The old `|| PAPER_W` fallback
        // pinned containerW to the full paper width whenever a measurement read 0
        // (a transient during SPA navigation / before layout settles), which forced
        // paperScale to 1 and made the paper overflow the viewport on mobile.
        const update = () => { const w = el.clientWidth; if (w > 0) setContainerW(w); };
        update();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        ro?.observe(el);
        window.addEventListener('resize', update);
        return () => { ro?.disconnect(); window.removeEventListener('resize', update); };
    }, [selectedList.length > 0, PAPER_W]);

    useLayoutEffect(() => {
        if (blocks.length === 0) { setPages([]); return; }
        const container = measureRef.current;
        const els = container ? [...container.querySelectorAll('[data-measure-block]')] : [];
        if (els.length !== blocks.length) { setPages([blocks]); return; }
        // One A4 page's content height at the current sheet width (A4 is 210×297mm),
        // minus the sheet's own vertical padding.
        const cs = getComputedStyle(container);
        const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
        const PAGE_AVAIL_PX = PAPER_W * (297 / 210) - padY;
        const pagesAcc = [];
        let current = [];
        let pageTop = 0; // page 1 baseline 0 so the header counts against its budget
        els.forEach((el, i) => {
            const bottom = el.offsetTop + el.offsetHeight;
            if (current.length && bottom - pageTop > PAGE_AVAIL_PX) {
                pagesAcc.push(current);
                current = [];
                pageTop = el.offsetTop;
            }
            current.push(blocks[i]);
        });
        if (current.length) pagesAcc.push(current);
        setPages(pagesAcc);
    }, [blocks, institution, examTitle, cls, subject, examTime, totalMarks, isBn, imgTick, PAPER_W, fontScale, logo, setNoEnabled, setNo, answerMode, footerText, headerNoteEnabled, headerNote, questionGap, mcqColumns, template]);

    // Physical sheets: normally one page per sheet; joint mode pairs consecutive
    // pages (continuous flow) onto one A4-landscape sheet — left = page 1, right = page 2.
    const sheets = useMemo(() => {
        if (!isJoint) return pages.map((p) => [p]);
        const out = [];
        for (let i = 0; i < pages.length; i += 2) out.push(pages.slice(i, i + 2));
        return out;
    }, [pages, isJoint]);

    // Keep the active sheet index in range as the paper grows/shrinks.
    useEffect(() => {
        setActivePage((p) => Math.min(Math.max(p, 0), Math.max(0, sheets.length - 1)));
    }, [sheets.length]);

    // The paper may not exceed MAX_PAGES A4 pages.
    const overLimit = pages.length > MAX_PAGES;

    // Compact pagination model: first, last, current ±1, with ellipsis gaps.
    const pageItems = useMemo(() => {
        const total = sheets.length;
        const current = activePage + 1; // 1-based
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const items = [1];
        const left = Math.max(2, current - 1);
        const right = Math.min(total - 1, current + 1);
        if (left > 2) items.push('…');
        for (let i = left; i <= right; i += 1) items.push(i);
        if (right < total - 1) items.push('…');
        items.push(total);
        return items;
    }, [sheets.length, activePage]);

    // One-click "Download PDF": renders each page to an image via html-to-image
    // (browser-native SVG rendering → pixel-perfect KaTeX math, unlike html2canvas)
    // and saves a multi-page PDF directly — no print dialog.
    const handleDownload = async () => {
        if (selectedList.length === 0) {
            notifications.show({
                title: t('common.error'),
                message: t('questionsCreate.toast.selectFirst'),
                color: 'orange',
            });
            return;
        }
        if (overLimit) {
            notifications.show({
                title: t('common.error'),
                message: t('questionsCreate.toast.pageLimit', { max: num(MAX_PAGES) }),
                color: 'orange',
            });
            return;
        }
        setEditingMarkId(null); // commit-safe: never capture an open mark input into the PDF
        setDownloading(true);
        // Read-only check first (an empty balance is refused up front); the credit is taken only AFTER the PDF was made.
        try {
            await questionPaperService.checkExport({ officeSpaceId });
        } catch (err) {
            setDownloading(false);
            if (err?.code === 'INSUFFICIENT_CREDITS') { setLimitModalOpen(true); return; }
            notifications.show({ title: t('common.error'), message: err?.message || t('questionsCreate.toast.failed'), color: 'red' });
            return;
        }
        try {
            recordToolUsage('questions-create', { category: 'result' });
            const base = (examTitle.trim() || t('questionsCreate.paper.defaultTitle'))
                .replace(/\s+/g, '-')
                .slice(0, 40);
            await generateQuestionPaperPdf(previewRef.current, `${base}-question-paper`, pageSize, SHEET_W);
            await questionPaperService.recordExport({ paperId: paperId || null, title: examTitle.trim() || null, officeSpaceId }).catch(() => {}); // the PDF exists → now the credit
            getCurrentUser().catch(() => {}); // refresh the shown credit balance
            notifications.show({
                title: t('common.success'),
                message: t('questionsCreate.toast.downloaded'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('questionsCreate.toast.failed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    // Open the print-preview modal (no credit charged yet — only on confirm).
    const handlePrint = () => {
        if (selectedList.length === 0) {
            notifications.show({
                title: t('common.error'),
                message: t('questionsCreate.toast.selectFirst'),
                color: 'orange',
            });
            return;
        }
        if (overLimit) {
            notifications.show({
                title: t('common.error'),
                message: t('questionsCreate.toast.pageLimit', { max: num(MAX_PAGES) }),
                color: 'orange',
            });
            return;
        }
        setEditingMarkId(null); // commit-safe: never print an open mark input
        setPrintPreviewOpen(true);
    };

    // Populate the modal's iframe the instant it mounts (callback ref) — a plain
    // effect can run before the Radix portal attaches the ref and then bail,
    // leaving the preview blank. The iframe only mounts while the modal is open.
    const attachPrintIframe = useCallback((iframe) => {
        if (!iframe) {
            // Unmount (modal closed): release the print document's Blob URL.
            const prev = printIframeRef.current;
            if (prev?._printBlobUrl) { URL.revokeObjectURL(prev._printBlobUrl); prev._printBlobUrl = null; }
            printIframeRef.current = null;
            return;
        }
        printIframeRef.current = iframe;
        setPrintPreviewLoading(true);
        const title = examTitle.trim() || t('questionsCreate.paper.defaultTitle');
        renderPaperIntoIframe(previewRef.current, iframe, { title, pageFormat: pageSize, contentPx: SHEET_W })
            .catch((err) => { console.error('Print preview render failed:', err); })
            .finally(() => setPrintPreviewLoading(false));
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [pageSize, SHEET_W]);

    // Confirm print: read-only credit check, print the iframe, THEN charge 1 credit (same price as an export).
    const handleConfirmPrint = async () => {
        setPrinting(true);
        try {
            await questionPaperService.checkExport({ officeSpaceId });
        } catch (err) {
            setPrinting(false);
            if (err?.code === 'INSUFFICIENT_CREDITS') { setPrintPreviewOpen(false); setLimitModalOpen(true); return; }
            notifications.show({ title: t('common.error'), message: err?.message || t('questionsCreate.toast.failed'), color: 'red' });
            return;
        }
        try {
            recordToolUsage('questions-create', { category: 'result' });
            const win = printIframeRef.current?.contentWindow;
            win?.focus();
            win?.print();
            await questionPaperService.recordExport({ paperId: paperId || null, title: examTitle.trim() || null, officeSpaceId }).catch(() => {}); // printed → now the credit
            getCurrentUser().catch(() => {}); // refresh the shown credit balance
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('questionsCreate.toast.failed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    // Auto-trigger a PDF export when opened via the list page's export action
    // (/questions-create/:id?export=1) — waits until the paper is fully loaded.
    // No cleanup: a later dep change (pagination settling) must NOT cancel it.
    useEffect(() => {
        if (autoExportedRef.current || searchParams.get('export') !== '1') return;
        if (loadingPaper || selectedList.length === 0 || pages.length === 0 || overLimit) return;
        autoExportedRef.current = true;
        setSearchParams({}, { replace: true }); // drop ?export=1 so a refresh won't re-charge
        setTimeout(() => handleDownload(), 400); // let the paper + images paint
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [searchParams, loadingPaper, selectedList.length, pages.length, overLimit]);

    // ── Load a saved paper when editing (/questions-create/:id) ──────
    useEffect(() => {
        if (!routeId) return undefined;
        let cancelled = false;
        setLoadingPaper(true);
        questionPaperService
            .get(routeId)
            .then((res) => {
                if (cancelled) return;
                const paper = res?.data;
                if (!paper) return;
                const questions = withStableId(paper.questions);
                setSelected(Object.fromEntries(questions.map((q) => [q.id, q])));
                setOrderIds(questions.map((q) => q.id));
                setExamTitle(paper.title || '');
                const s = paper.settings || {};
                setInstitution(s.institution || '');
                setExamTime(s.examTime || '');
                setCls(s.cls || '');
                setSubject(s.subject || '');
                setChapter(s.chapter || '');
                setSectionMarks(s.sectionMarks || { ...DEFAULT_MARKS });
                setMarksById(s.marksById || {});
                setTemplate(s.template || DEFAULT_TEMPLATE_ID);
                setPageSize(s.pageSize || 'a4');
                setFontScale(s.fontScale || 1);
                setLogo(s.logo || '');
                setSetNoEnabled(Boolean(s.setNoEnabled));
                setSetNo(s.setNo || '');
                setAnswerMode(Boolean(s.answerMode));
                setFooterText(s.footerText || '');
                setHeaderNoteEnabled(Boolean(s.headerNoteEnabled));
                setHeaderNote(s.headerNote || '');
                setQuestionGap(s.questionGap || 'normal');
                setMcqColumns(s.mcqColumns === 2 ? 2 : 1);
                setWatermarkText(s.watermarkText || '');
                setWatermarkType(s.watermarkType === 'image' ? 'image' : 'text');
                setWatermarkImage(s.watermarkImage || '');
                setWatermarkPos(s.watermarkPos || 'center');
                setPaperId(paper.id);
                setFeedbackState(paper.feedback || null);
            })
            .catch((err) => {
                if (cancelled) return;
                notifications.show({
                    title: t('common.error'),
                    message: err?.message || t('questionsCreate.loadFailed'),
                    color: 'red',
                });
            })
            .finally(() => { if (!cancelled) setLoadingPaper(false); });
        return () => { cancelled = true; };
    }, [routeId]);

    // ── Logo upload (compressed to ≤50KB on the server, stored on R2) ──
    const handleLogoPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            notifications.show({ title: t('common.error'), message: t('questionsCreate.logo.invalid'), color: 'orange' });
            return;
        }
        setUploadingLogo(true);
        try {
            const res = await questionPaperService.uploadLogo(file);
            const url = res?.data?.url;
            if (url) setLogo(url);
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('questionsCreate.logo.failed'), color: 'red' });
        } finally {
            setUploadingLogo(false);
        }
    };

    // ── Watermark image upload (reuses the logo endpoint → R2) ─────────
    const handleWatermarkPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            notifications.show({ title: t('common.error'), message: t('questionsCreate.logo.invalid'), color: 'orange' });
            return;
        }
        setUploadingWatermark(true);
        try {
            const res = await questionPaperService.uploadLogo(file);
            const url = res?.data?.url;
            if (url) setWatermarkImage(url);
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('questionsCreate.logo.failed'), color: 'red' });
        } finally {
            setUploadingWatermark(false);
        }
    };

    // ── Save / update the paper ──────────────────────────────────────
    const handleSave = async () => {
        if (selectedList.length === 0) {
            notifications.show({
                title: t('common.error'),
                message: t('questionsCreate.toast.selectFirst'),
                color: 'orange',
            });
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: examTitle.trim() || t('questionsCreate.paper.defaultTitle'),
                questions: selectedList,
                settings: { institution, examTime, cls, subject, chapter, sectionMarks, marksById, template, totalMarks, pageSize, fontScale, logo, setNoEnabled, setNo, answerMode, footerText, headerNoteEnabled, headerNote, questionGap, mcqColumns, watermarkText, watermarkType, watermarkImage, watermarkPos },
                officeSpaceId,
            };
            const isNew = !paperId;
            const res = isNew
                ? await questionPaperService.create(payload)
                : await questionPaperService.update(paperId, payload);
            const saved = res?.data;
            if (isNew && saved?.id) {
                setPaperId(saved.id);
                navigate(`/questions-create/${saved.id}`, { replace: true });
                getCurrentUser().catch(() => {}); // creating a paper spent 1 credit
            }
            notifications.show({
                title: t('common.success'),
                message: t('questionsCreate.toast.saved'),
                color: 'green',
            });
        } catch (err) {
            if (err?.code === 'INSUFFICIENT_CREDITS') { setLimitModalOpen(true); return; }
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('questionsCreate.toast.saveFailed'),
                color: 'red',
            });
        } finally {
            setSaving(false);
        }
    };

    // ── Paper feedback (like / dislike after Save) ───────────────────
    // One-shot per paper; a dislike collects a reason (+ optional note) in the shared
    // modal. Keys must match ALLOWED_FEEDBACK_REASONS on the backend.
    const QP_FEEDBACK_REASONS = ['wrong_question', 'wrong_answer', 'repeated_question', 'formatting_issue', 'other'];
    const submitPaperFeedback = async (feedback, reason = null, note = null) => {
        if (!paperId || feedbackLoading) return;
        setFeedbackLoading(feedback);
        try {
            await questionPaperService.submitFeedback(paperId, feedback, reason, note);
            setFeedbackState(feedback);
            setShowFeedbackModal(false);
            notifications.show({ message: t('worldCupJersey.feedback.thanks'), color: 'green', autoClose: 3000 });
        } catch (err) {
            notifications.show({ message: t('worldCupJersey.feedback.failed'), color: 'red' });
        } finally {
            setFeedbackLoading(null);
        }
    };
    const handleLike = () => { if (!feedbackState && !feedbackLoading) submitPaperFeedback('positive'); };
    const handleDislike = () => { if (!feedbackState && !feedbackLoading) setShowFeedbackModal(true); };

    // ── Controls (left sidebar) ──────────────────────────────────────
    // ── Auto Create panel (random paper from multiple chapters) ──────
    const autoChapterOptions = chaptersFor(filterTree, cls, subject);
    const toggleAutoChapter = (ch) => setAutoChapters((prev) => (prev.includes(ch) ? prev.filter((x) => x !== ch) : [...prev, ch]));
    const toggleAutoType = (tp) => setAutoTypes((prev) => (prev.includes(tp) ? prev.filter((x) => x !== tp) : [...prev, tp]));
    const autoCanGenerate = !!subject && autoChapters.length > 0 && autoTypes.length > 0 && Number(bnToEn(autoCount)) > 0;
    const chipCls = (on) => `rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${on ? 'border-primary bg-primary/10 text-primary' : 'border-zinc-200 text-zinc-600 hover:border-primary/40 dark:border-zinc-700 dark:text-zinc-300'}`;
    const autoPanel = (
        <div className="space-y-3">
            <SelectField value={cls} onChange={onClassChange} options={classOpts} placeholder={t('questionsCreate.filters.class')} />
            <SelectField value={subject} onChange={onSubjectChange} options={subjectOpts} placeholder={t('questionsCreate.filters.subject')} disabled={!cls} />

            {/* Multiple chapters */}
            <div>
                <label className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('questionsCreate.auto.chapters')}</label>
                {!subject ? (
                    <p className="text-xs text-zinc-400">{t('questionsCreate.auto.selectSubjectFirst')}</p>
                ) : autoChapterOptions.length === 0 ? (
                    <p className="text-xs text-zinc-400">{t('questionsCreate.auto.noChapters')}</p>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {autoChapterOptions.map((ch) => (
                            <button key={ch} type="button" onClick={() => toggleAutoChapter(ch)} className={chipCls(autoChapters.includes(ch))}>
                                {ch}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Question types to include */}
            <div>
                <label className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('questionsCreate.auto.types')}</label>
                <div className="flex flex-wrap gap-1.5">
                    {TYPE_ORDER.map((tp) => (
                        <button key={tp} type="button" onClick={() => toggleAutoType(tp)} className={chipCls(autoTypes.includes(tp))}>
                            {typeLabel(tp)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Max total questions */}
            <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('questionsCreate.auto.maxQuestions')}</label>
                <input
                    value={autoCount}
                    onChange={(e) => { const en = bnToEn(e.target.value).replace(/[^0-9]/g, '').slice(0, 3); setAutoCount(isBn ? toBnDigits(en) : en); }}
                    inputMode="numeric"
                    className={`${inputClass} w-20 px-2.5 py-1.5`}
                />
            </div>

            {autoError && <p className="text-xs text-red-500">{autoError}</p>}

            <button
                type="button"
                onClick={generateAutoPaper}
                disabled={!autoCanGenerate || autoGenerating}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
                {autoGenerating ? <IconLoader2 size={16} className="animate-spin" /> : <IconArrowsShuffle size={16} />}
                {t('questionsCreate.auto.generate')}
            </button>
            <p className="text-[11px] text-zinc-400">{t('questionsCreate.auto.replaceHint')}</p>
        </div>
    );

    const controls = (
        <div className="space-y-4">
            {/* Create mode — browse & pick vs auto-generate */}
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-zinc-100 p-0.5 dark:bg-zinc-800">
                {[['manual', t('questionsCreate.auto.manualMode')], ['auto', t('questionsCreate.auto.autoMode')]].map(([m, label]) => (
                    <button
                        key={m}
                        type="button"
                        onClick={() => setBrowseMode(m)}
                        className={`rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${browseMode === m ? 'bg-white text-primary shadow-sm dark:bg-zinc-700' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400'}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Typing your own questions is a separate page — keep the way there in plain sight */}
            <button
                type="button"
                onClick={() => navigate('/questions-create/manual')}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/40 px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/5"
            >
                <IconPencil size={14} /> {t('questionsCreate.manual.writeOwn')}
            </button>

            {browseMode === 'auto' ? autoPanel : (
              <>
            {/* Collapsible filter section */}
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
                <button
                    type="button"
                    onClick={() => setFiltersOpen((v) => !v)}
                    aria-expanded={filtersOpen}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 bg-zinc-50 dark:bg-zinc-800/60 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-zinc-700 dark:text-zinc-200">
                        <IconAdjustmentsHorizontal size={16} className="text-primary" />
                        {t('questionsCreate.filterSection')}
                    </span>
                    <IconChevronDown
                        size={16}
                        className={`text-zinc-400 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                    />
                </button>

                <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                        filtersOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                >
                  <div className="overflow-hidden">
                    <div className="space-y-2 p-3">
                        {/* Filters */}
                        <SelectField
                            value={cls}
                            onChange={onClassChange}
                            options={classOpts}
                            placeholder={t('questionsCreate.filters.class')}
                        />
                        <SelectField
                            value={subject}
                            onChange={onSubjectChange}
                            options={subjectOpts}
                            placeholder={t('questionsCreate.filters.subject')}
                            disabled={!cls}
                        />
                        <SelectField
                            value={chapter}
                            onChange={setChapter}
                            options={chapterOpts}
                            placeholder={t('questionsCreate.filters.chapter')}
                            disabled={!subject}
                        />
                        <SelectField
                            value={type}
                            onChange={setType}
                            options={typeOpts}
                            placeholder={t('questionsCreate.filters.allTypes')}
                            disabled={!chapter}
                        />

                        {/* Search */}
                        <div className="relative">
                            <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                                placeholder={t('questionsCreate.searchPlaceholder')}
                                className={`${inputClass} pl-9`}
                            />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                        aria-label={t('common.clear')}
                    >
                                <IconX size={15} />
                            </button>
                        )}
                        </div>

                        {/* Apply — questions are fetched only on click, not per filter change */}
                        <button
                            type="button"
                            onClick={applyFilters}
                            disabled={loadingResults || !chapter}
                            className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loadingResults ? (
                                <><IconLoader2 size={16} className="animate-spin" /> {t('questionsCreate.applying')}</>
                            ) : (
                                <><IconFilterSearch size={16} /> {t('questionsCreate.applyFilter')}</>
                            )}
                        </button>
                    </div>
                  </div>
                </div>
            </div>

            {/* Result list actions */}
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                    {isRefining
                        ? t('questionsCreate.filteredCount', { count: num(visibleResults.length), total: num(results.length) })
                        : resultsTotal > results.length
                            ? t('questionsCreate.showingOfTotal', { count: num(results.length), total: num(resultsTotal) })
                            : t('questionsCreate.foundCount', { count: num(results.length) })}
                </span>
                <button
                    type="button"
                    onClick={toggleSelectAllVisible}
                    disabled={visibleResults.length === 0}
                    className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                    {allVisibleSelected
                        ? <><IconDeselect size={13} /> {t('questionsCreate.deselectAll')}</>
                        : <><IconSelect size={13} /> {t('questionsCreate.selectAll')}</>}
                </button>
            </div>

            {/* In-list refinement: search within results + quick type chips.
                Shown only once a non-empty result set has been fetched. */}
            {hasApplied && !loadingResults && !resultsError && results.length > 0 && (
                <div className="space-y-2">
                    <div className="relative">
                        <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input
                            value={listSearch}
                            onChange={(e) => setListSearch(e.target.value)}
                            placeholder={t('questionsCreate.listSearchPlaceholder')}
                            className={`${inputClass} pl-9`}
                        />
                        {listSearch && (
                            <button
                                type="button"
                                onClick={() => setListSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                                aria-label={t('common.clear')}
                            >
                                <IconX size={15} />
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => setActiveTypes([])}
                            className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                activeTypes.length === 0
                                    ? 'border-primary bg-primary text-white'
                                    : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800'
                            }`}
                        >
                            {t('questionsCreate.chipAll')}
                        </button>
                        {TYPE_ORDER.filter((tp) => typeCounts[tp] > 0).map((tp) => {
                            const active = activeTypes.includes(tp);
                            return (
                                <button
                                    key={tp}
                                    type="button"
                                    onClick={() => toggleActiveType(tp)}
                                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                        active
                                            ? 'border-primary bg-primary text-white'
                                            : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800'
                                    }`}
                                >
                                    {typeLabel(tp)} ({num(typeCounts[tp])})
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Question list */}
            <div className="max-h-[640px] space-y-1.5 overflow-y-auto pr-0.5">
                {loadingResults ? (
                    <p className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-zinc-200 py-8 text-center text-xs text-zinc-400 dark:border-zinc-700">
                        <IconLoader2 size={14} className="animate-spin text-primary" />
                        {t('questionsCreate.loading')}
                    </p>
                ) : resultsError ? (
                    <p className="rounded-lg border border-dashed border-red-200 py-8 text-center text-xs text-red-500 dark:border-red-800/60">
                        {resultsError}
                    </p>
                ) : !hasApplied ? (
                    <p className="rounded-lg border border-dashed border-zinc-200 py-8 text-center text-xs text-zinc-400 dark:border-zinc-700">
                        {t('questionsCreate.startHint')}
                    </p>
                ) : visibleResults.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-zinc-200 py-8 text-center text-xs text-zinc-400 dark:border-zinc-700">
                        {t('questionsCreate.noResults')}
                    </p>
                ) : (
                    visibleResults.map((q) => {
                        const isSel = Boolean(selected[q.id]);
                        return (
                            <Tooltip key={q.id}>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => toggle(q)}
                                        className={`flex w-full items-start gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                                            isSel
                                                ? 'border-primary/50 bg-primary/5 dark:bg-primary/10'
                                                : 'border-zinc-200 bg-white hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:hover:bg-zinc-700/50'
                                        }`}
                                    >
                                        <span className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded border ${
                                            isSel ? 'border-primary bg-primary text-white' : 'border-zinc-300 dark:border-zinc-600'
                                        }`}>
                                            {isSel && <IconSquareCheck size={12} />}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-[12.5px] leading-snug text-zinc-700 dark:text-zinc-200"><MathText text={q.text} /></span>
                                            {questionImages(q).length > 0 && (
                                                <span className="mt-1 flex flex-wrap gap-1.5">
                                                    {questionImages(q).map((src, i) => (
                                                        <img
                                                            key={i}
                                                            src={src}
                                                            alt=""
                                                            loading="lazy"
                                                            className="h-12 w-auto rounded border border-zinc-200 object-contain dark:border-zinc-700"
                                                        />
                                                    ))}
                                                </span>
                                            )}
                                            <span className="mt-0.5 inline-flex items-center gap-1.5 text-[10.5px] text-zinc-400">
                                                <span className="rounded bg-zinc-100 px-1.5 py-px font-medium text-zinc-500 dark:bg-zinc-700 dark:text-zinc-300">{typeLabel(q.type)}</span>
                                                <span>{t('questionsCreate.markUnit', { count: num(markOf(q)) })}</span>
                                            </span>
                                        </span>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side={tipSide} align="start" collisionPadding={12} className="max-w-[min(260px,92vw)] font-hind">
                                    <div className="space-y-1 text-[11.5px] leading-relaxed">
                                        <div className="font-semibold"><MathText text={q.text} /></div>
                                        {q.subject && <p><span className="opacity-60">{t('questionsCreate.paper.subject')}:</span> {q.subject}</p>}
                                        {q.chapter && <p><span className="opacity-60">{t('questionsCreate.chapterLabel')}:</span> {q.chapter}</p>}
                                        <p className="flex flex-wrap gap-x-3">
                                            <span><span className="opacity-60">{t('questionsCreate.questionType')}:</span> {typeLabel(q.type)}</span>
                                            <span>{t('questionsCreate.markUnit', { count: num(markOf(q)) })}</span>
                                        </p>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })
                )}
            </div>
              </>
            )}
        </div>
    );

    // ── Manual entry panel (replaces the question-bank filters in manual mode) ──
    const manualInputCls = 'w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40';
    const manualPanel = (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 p-3 sm:p-4 space-y-3">
            <div className="flex items-center gap-1.5 text-[13px] font-semibold text-zinc-700 dark:text-zinc-200">
                <IconSquareCheck size={16} className="text-primary" />
                {t('questionsCreate.manual.title')}
            </div>

            {/* Question type */}
            <div className="grid grid-cols-3 gap-1 rounded-lg bg-zinc-100 p-0.5 dark:bg-zinc-800">
                {TYPE_ORDER.map((tp) => (
                    <button
                        key={tp}
                        type="button"
                        onClick={() => { setManualType(tp); setManualCorrect(null); }}
                        className={`rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${manualType === tp ? 'bg-white text-primary shadow-sm dark:bg-zinc-700' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'}`}
                    >
                        {typeLabel(tp)}
                    </button>
                ))}
            </div>

            {/* Question text — for srijonshil (broad) this is the stimulus/passage */}
            <div>
                <label className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">
                    {manualType === 'broad' ? t('questionsCreate.manual.stimulus') : t('questionsCreate.manual.questionText')}
                </label>
                <textarea
                    rows={manualType === 'broad' ? 4 : 3}
                    value={manualText}
                    onChange={(e) => setManualText(e.target.value)}
                    placeholder={manualType === 'broad' ? t('questionsCreate.manual.stimulusPlaceholder') : t('questionsCreate.manual.questionPlaceholder')}
                    className={`${manualInputCls} resize-y`}
                />
                <p className="mt-1 text-[11px] text-zinc-400">{t('questionsCreate.manual.mathHint')}</p>
            </div>

            {/* Srijonshil (creative/board) sub-questions ক/খ/গ/ঘ — broad type only */}
            {manualType === 'broad' && (
                <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('questionsCreate.manual.subQuestions')}</label>
                    {manualParts.map((part, i) => (
                        <div key={i} className="flex items-start gap-2">
                            <span className="mt-1.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10 text-[12px] font-bold text-primary">
                                {BROAD_SUB_LETTERS[i] ?? i + 1}
                            </span>
                            <textarea
                                rows={1}
                                value={part}
                                onChange={(e) => setManualParts((prev) => prev.map((p, idx) => (idx === i ? e.target.value : p)))}
                                placeholder={`${t('questionsCreate.manual.subQuestion')} (${BROAD_SUB_LETTERS[i] ?? i + 1})`}
                                className={`${manualInputCls} resize-y px-2.5 py-1.5`}
                            />
                            {manualParts.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setManualParts((prev) => prev.filter((_, idx) => idx !== i))}
                                    className="mt-1.5 flex-none text-zinc-300 hover:text-red-500 dark:text-zinc-500"
                                    aria-label={t('common.delete')}
                                >
                                    <IconTrash size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                    {manualParts.length < BROAD_SUB_LETTERS.length && (
                        <button
                            type="button"
                            onClick={() => setManualParts((prev) => [...prev, ''])}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
                        >
                            <IconPlus size={13} /> {t('questionsCreate.manual.addSubQuestion')}
                        </button>
                    )}
                </div>
            )}

            {/* MCQ options */}
            {manualType === 'mcq' && (
                <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('questionsCreate.manual.options')}</label>
                    {manualOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setManualCorrect(manualCorrect === i ? null : i)}
                                title={t('questionsCreate.manual.markCorrect')}
                                className={`flex h-6 w-6 flex-none items-center justify-center rounded-full border text-[11px] font-semibold transition-colors ${manualCorrect === i ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-zinc-300 text-zinc-400 hover:border-primary hover:text-primary dark:border-zinc-600'}`}
                            >
                                {manualCorrect === i ? <IconCircleCheck size={14} /> : (optionLetters[i] ?? i + 1)}
                            </button>
                            <input
                                value={opt}
                                onChange={(e) => setManualOptions((prev) => prev.map((o, idx) => (idx === i ? e.target.value : o)))}
                                placeholder={`${t('questionsCreate.manual.option')} ${num(i + 1)}`}
                                className={`${manualInputCls} px-2.5 py-1.5`}
                            />
                            {manualOptions.length > 2 && (
                                <button
                                    type="button"
                                    onClick={() => { setManualOptions((prev) => prev.filter((_, idx) => idx !== i)); setManualCorrect((c) => (c === i ? null : c != null && c > i ? c - 1 : c)); }}
                                    className="flex-none text-zinc-300 hover:text-red-500 dark:text-zinc-500"
                                    aria-label={t('common.delete')}
                                >
                                    <IconTrash size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                    {manualOptions.length < 6 && (
                        <button
                            type="button"
                            onClick={() => setManualOptions((prev) => [...prev, ''])}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
                        >
                            <IconPlus size={13} /> {t('questionsCreate.manual.addOption')}
                        </button>
                    )}
                </div>
            )}

            {/* Optional marks override */}
            <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('questionsCreate.manual.marks')}</label>
                <input
                    value={manualMarks}
                    onChange={(e) => { const en = bnToEn(e.target.value).replace(/[^0-9]/g, ''); setManualMarks(isBn ? toBnDigits(en) : en); }}
                    placeholder={num(sectionMarks[manualType] ?? DEFAULT_MARKS[manualType] ?? 1)}
                    className={`${manualInputCls} w-20 px-2.5 py-1.5`}
                />
            </div>

            <button
                type="button"
                onClick={addManualQuestion}
                disabled={!manualCanAdd}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <IconPlus size={16} /> {t('questionsCreate.manual.addToPaper')}
            </button>
        </div>
    );

    // ── Settings card (below controls): selection summary ────────────
    const settings = (
        <div className="rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
            <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-700 dark:text-zinc-200">
                    <IconChecklist size={16} className="text-primary" />
                    {t('questionsCreate.selectedCount', { count: num(selectedList.length) })}
                </span>
                {selectedList.length > 0 && (
                    <button
                        type="button"
                        onClick={clearSelection}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600"
                    >
                        <IconTrash size={13} /> {t('common.clear')}
                    </button>
                )}
            </div>
            <p className="mt-1 text-xs text-zinc-400">
                {t('questionsCreate.totalMarks', { count: num(totalMarks) })}
            </p>

            {/* Bulk marks per section — applies to every question of that type,
                except ones given an inline override in the preview. */}
            {grouped.length > 0 && (
                <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                    <p className="mb-2 text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                        {t('questionsCreate.marksPerSection')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {grouped.map((sec) => (
                            <label
                                key={sec.type}
                                className="flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 px-2 py-1 dark:border-zinc-700 dark:bg-zinc-800"
                            >
                                <span className="text-[11px] text-zinc-500 dark:text-zinc-400">{typeLabel(sec.type)}</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={sectionMarks[sec.type]}
                                    onChange={(e) => {
                                        const n = Math.max(0, Math.round(Number(e.target.value) || 0));
                                        setSectionMarks((prev) => ({ ...prev, [sec.type]: n }));
                                    }}
                                    className="w-12 rounded border border-zinc-200 bg-white px-1.5 py-0.5 text-right text-[12px] text-zinc-700 outline-none focus:ring-2 focus:ring-primary/40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </label>
                        ))}
                    </div>
                    <p className="mt-1.5 text-[10.5px] text-zinc-400">{t('questionsCreate.marksHint')}</p>
                </div>
            )}
        </div>
    );

    // ── Preview (right): printable paper ─────────────────────────────
    const preview = (
        <div className="flex min-h-[min(70vh,760px)] flex-col rounded-2xl border border-zinc-200 bg-zinc-100 p-[15px] dark:border-zinc-700 dark:bg-zinc-800">
            <div className="flex-1">
                {selectedList.length === 0 ? (
                    <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-3 text-center">
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-primary shadow-sm dark:bg-zinc-900">
                            <IconChecklist size={28} stroke={1.6} />
                        </span>
                        <p className="text-sm font-semibold text-zinc-600 dark:text-zinc-300">{t('questionsCreate.previewEmpty')}</p>
                        <p className="max-w-xs text-xs text-zinc-400">{t('questionsCreate.previewEmptyHint')}</p>
                    </div>
                ) : (
                  <>
                    {/* Over-limit warning */}
                    {overLimit && (
                        <div style={paperWidthStyle} className="mx-auto mb-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/20">
                            <IconAlertTriangle size={17} className="mt-0.5 flex-none text-amber-500" />
                            <span className="text-[12.5px] leading-relaxed text-amber-700 dark:text-amber-300">
                                {t('questionsCreate.pageLimitWarning', { count: num(pages.length), max: num(MAX_PAGES) })}
                            </span>
                        </div>
                    )}

                    {/* Page switcher + drag hint — outside previewRef so they never export */}
                    <div style={paperWidthStyle} className="mx-auto mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 text-[11.5px] text-zinc-400 dark:text-zinc-500">
                            <IconGripVertical size={13} />
                            {t('questionsCreate.dragHint')}
                        </div>
                        {sheets.length > 1 && (
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setActivePage((p) => Math.max(0, p - 1))}
                                    disabled={activePage === 0}
                                    aria-label={t('common.previous')}
                                    className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-transparent dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                >
                                    <IconChevronLeft size={15} />
                                </button>
                                {pageItems.map((it, idx) => (
                                    it === '…' ? (
                                        <span key={`e${idx}`} className="px-1 text-xs text-zinc-400">…</span>
                                    ) : (
                                        <button
                                            key={it}
                                            type="button"
                                            onClick={() => setActivePage(it - 1)}
                                            className={`flex h-7 min-w-[28px] items-center justify-center rounded-md px-1.5 text-xs font-medium transition-colors ${
                                                it - 1 === activePage
                                                    ? 'bg-primary text-white'
                                                    : 'border border-zinc-200 text-zinc-600 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-700'
                                            }`}
                                        >
                                            {num(it)}
                                        </button>
                                    )
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setActivePage((p) => Math.min(sheets.length - 1, p + 1))}
                                    disabled={activePage === sheets.length - 1}
                                    aria-label={t('common.next')}
                                    className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-transparent dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                >
                                    <IconChevronRight size={15} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* All pages live in previewRef; only the active one is visible on screen.
                        The paper is rendered at a FIXED PAPER_W and visually scaled with CSS
                        `zoom` to fit the available width — `zoom` (unlike transform) shrinks the
                        layout box too, so there's no empty space and pagination stays width-independent.
                        The PDF export re-renders each page at PAPER_W and strips this, so it's unaffected. */}
                    <div ref={paperScrollRef} className="w-full max-w-full overflow-x-auto">
                    <div
                        ref={previewRef}
                        style={{ width: SHEET_W, zoom: paperScale }}
                        className={`mx-auto ${dragId ? 'cursor-grabbing select-none' : ''}`}
                    >
                        {sheets.map((sheetPages, si) => {
                            // Faint diagonal watermark — text or image (exports into the
                            // PDF). One per page; in joint mode one per half-page column.
                            const watermarkLayer = () => (
                                ((watermarkType === 'image' && watermarkImage) || (watermarkType !== 'image' && watermarkText.trim())) && (
                                    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 z-0 flex overflow-hidden p-10 ${WM_POS[watermarkPos] || WM_POS.center}`}>
                                        {watermarkType === 'image' ? (
                                            <img
                                                src={watermarkImage}
                                                alt=""
                                                className="max-h-[45%] max-w-[60%] select-none object-contain opacity-[0.07]"
                                                style={{ transform: 'rotate(-32deg)' }}
                                            />
                                        ) : (
                                            // Long text wraps onto a second line instead of overflowing off the page.
                                            <span
                                                className="select-none whitespace-normal break-words text-center font-bold uppercase leading-tight tracking-[0.2em] text-zinc-900/[0.06]"
                                                style={{ transform: 'rotate(-32deg)', fontSize: `${Math.round(64 * fontScale)}px`, maxWidth: '75%' }}
                                            >
                                                {watermarkText}
                                            </span>
                                        )}
                                    </div>
                                )
                            );
                            return (
                                <div
                                    key={si}
                                    ref={animateRef}
                                    data-paper-page
                                    className={`relative overflow-hidden bg-white text-zinc-900 shadow-sm ${isJoint ? 'flex items-stretch' : 'px-6 py-7 sm:px-9 sm:py-10'} ${si === activePage ? '' : 'hidden'}`}
                                    style={{ fontFamily: tpl.fontFamily }}
                                >
                                    {isJoint ? (
                                        <>
                                            {sheetPages.map((colBlocks, ci) => (
                                                <div
                                                    key={ci}
                                                    className={`relative w-1/2 overflow-hidden px-6 py-7 sm:px-9 sm:py-10 ${ci === 1 ? 'border-l border-dashed border-zinc-300' : ''}`}
                                                >
                                                    {watermarkLayer()}
                                                    <div className="relative z-10">
                                                        {si * 2 + ci === 0 && paperHeader(true)}
                                                        {colBlocks.map((b) => renderBlock(b, { draggable: true }))}
                                                    </div>
                                                </div>
                                            ))}
                                            {/* Odd page count: keep the sheet full width with a blank right half */}
                                            {sheetPages.length === 1 && (
                                                <div aria-hidden="true" className="w-1/2 border-l border-dashed border-zinc-300" />
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {watermarkLayer()}
                                            <div className="relative z-10">
                                                {si === 0 && paperHeader(true)}
                                                {sheetPages[0].map((b) => renderBlock(b, { draggable: true }))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    </div>
                  </>
                )}
            </div>

            {/* Hidden measuring container — same 720px width + markup as the PDF export */}
            {selectedList.length > 0 && (
                <div
                    ref={measureRef}
                    aria-hidden="true"
                    data-qpaper-measure
                    className="pointer-events-none fixed left-[-20000px] top-0 bg-white px-6 py-7 text-zinc-900 sm:px-9 sm:py-10"
                    style={{ width: PAPER_W, fontFamily: tpl.fontFamily }}
                >
                    {paperHeader(false)}
                    {blocks.map((b) => renderBlock(b, { draggable: false, measure: true }))}
                </div>
            )}
        </div>
    );

    const disabledActions = selectedList.length === 0;

    // ── Loading skeletons (while a saved paper is being fetched) ──────
    const controlsSkeleton = (
        <div className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 sm:p-4">
            <Skel className="h-11 w-full rounded-xl" />
            {[0, 1, 2, 3].map((i) => <Skel key={i} className="h-10 w-full rounded-lg" />)}
            <Skel className="h-10 w-full rounded-lg bg-primary/20 dark:bg-primary/20" />
            <div className="space-y-1.5 pt-2">
                {[0, 1, 2, 3, 4].map((i) => <Skel key={i} className="h-12 w-full rounded-lg" />)}
            </div>
        </div>
    );
    const previewSkeleton = (
        <div className="flex h-full min-h-[min(70vh,760px)] flex-col rounded-2xl border border-zinc-200 bg-zinc-100 p-[15px] dark:border-zinc-700 dark:bg-zinc-800">
            <div style={paperWidthStyle} className="mx-auto w-full bg-white px-9 py-10 shadow-sm dark:bg-zinc-900">
                <Skel className="mx-auto h-6 w-1/2" />
                <Skel className="mx-auto mt-3 h-4 w-1/3" />
                <Skel className="mx-auto mt-1.5 h-4 w-1/4" />
                <Skel className="mt-4 h-px w-full" />
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="mt-5 space-y-2">
                        <Skel className="h-4 w-3/4" />
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pl-4">
                            <Skel className="h-3 w-2/3" />
                            <Skel className="h-3 w-2/3" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // ── Redesigned toolbar helpers (labeled columns · shared styling) ──
    const tbCol = 'flex flex-col gap-1.5'; // labeled column; separated by whitespace (no vertical rules)
    const tbHead = 'flex h-5 items-center gap-1.5'; // fixed-height label row → all columns align
    const tbLabel = 'text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500';
    const tbInput = 'h-7 rounded-md border border-zinc-200 bg-white px-2 text-xs text-zinc-700 outline-none transition-colors focus:ring-2 focus:ring-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';
    const Toggle = ({ on, onClick, label }) => (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={label}
            onClick={onClick}
            className={`relative h-4 w-7 flex-none rounded-full transition-colors ${on ? 'bg-primary' : 'bg-zinc-300 dark:bg-zinc-600'}`}
        >
            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${on ? 'left-3.5' : 'left-0.5'}`} />
        </button>
    );

    return (
        <TooltipProvider delayDuration={200}>
        <div className="w-full font-hind space-y-4 py-2 sm:py-4">
            {/* ── Header row (title left · actions right) ─────────────── */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                    <Tip label={t('questionsCreate.backToList')}>
                    <button
                        type="button"
                        onClick={() => navigate('/documents?tab=questions')}
                        aria-label={t('questionsCreate.backToList')}
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                        <IconArrowLeft size={17} />
                    </button>
                    </Tip>
                    <div className="min-w-0">
                        <h1 className="truncate text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-2xl">
                            {paperId ? t('questionsCreate.editTitle') : (manual ? t('questionsCreate.manual.pageTitle') : t('questionsCreate.newTitle'))}
                        </h1>
                        {loadingPaper ? (
                            <span className="inline-flex items-center gap-1 text-xs text-zinc-400">
                                <IconLoader2 size={12} className="animate-spin" /> {t('questionsCreate.loading')}
                            </span>
                        ) : selectedList.length > 0 ? (
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('questionsCreate.paperMeta', { count: num(selectedList.length), marks: num(totalMarks) })}
                            </p>
                        ) : (
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('questionsCreate.listSubtitle')}</p>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Template picker */}
                    <div className="flex items-center gap-1.5">
                        <IconLayoutGrid size={16} className="text-zinc-400" />
                        <div style={{ width: 150 }}>
                            <SelectField
                                value={template}
                                onChange={setTemplate}
                                options={PAPER_TEMPLATES.map((tp) => ({ value: tp.id, label: isBn ? tp.labelBn : tp.label }))}
                                placeholder={t('questionsCreate.template')}
                            />
                        </div>
                    </div>
                    {/* Save / Update */}
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || disabledActions}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                        {saving ? <IconLoader2 size={16} className="animate-spin" /> : <IconDeviceFloppy size={16} />}
                        {paperId ? t('questionsCreate.update') : t('questionsCreate.save')}
                    </button>
                    {/* Print */}
                    <button
                        type="button"
                        onClick={handlePrint}
                        disabled={disabledActions || overLimit}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                        <IconPrinter size={16} />
                        {t('questionsCreate.print')}
                    </button>
                    {/* Download PDF */}
                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={downloading || disabledActions || overLimit}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-primary/30 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {downloading ? <IconLoader2 size={16} className="animate-spin" /> : <IconDownload size={16} />}
                        {t('questionsCreate.download')}
                    </button>

                    {/* Like / dislike on the saved paper — appears after Save, one-shot.
                        Once rated, the control simply disappears (the submit toast is the
                        acknowledgement — no permanent "thanks" line). */}
                    {paperId && !feedbackState && (
                        <div className="inline-flex items-center gap-2">
                            <span className="hidden text-xs font-medium text-zinc-500 dark:text-zinc-400 sm:inline">{t('questionsCreate.feedbackPrompt')}</span>
                            <button
                                type="button"
                                onClick={handleLike}
                                disabled={!!feedbackLoading}
                                title={t('worldCupJersey.feedback.likeLabel')}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {feedbackLoading === 'positive' ? <IconLoader2 size={16} className="animate-spin" /> : <IconThumbUp size={16} />}
                                {t('worldCupJersey.feedback.likeLabel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleDislike}
                                disabled={!!feedbackLoading}
                                title={t('worldCupJersey.feedback.dislikeLabel')}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-red-500 px-3 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {feedbackLoading === 'negative' ? <IconLoader2 size={16} className="animate-spin" /> : <IconThumbDown size={16} />}
                                {t('worldCupJersey.feedback.dislikeLabel')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Body: sidebar (left) + [toolbar + preview] (right) ── */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(320px,390px)_1fr]">
                {loadingPaper ? controlsSkeleton : (
                    <div className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 sm:p-4">
                        {manual ? manualPanel : controls}
                        {settings}
                    </div>
                )}
                <div className="space-y-4">
                    {/* ── Paper layout toolbar — labeled columns · disabled until a question is added ── */}
                    <div
                        aria-disabled={disabledActions}
                        title={disabledActions ? t('questionsCreate.toolsDisabledHint') : undefined}
                        className={`rounded-xl border border-zinc-200 bg-white px-5 py-3.5 dark:border-zinc-700 dark:bg-zinc-900 ${disabledActions ? 'pointer-events-none select-none opacity-60' : ''}`}
                    >
                      <div className="flex flex-wrap items-stretch gap-x-8 gap-y-3">
                        {/* Logo */}
                        <div className={tbCol}>
                            <div className={tbHead}><span className={tbLabel}>{t('questionsCreate.logoLabel')}</span></div>
                            <div className="flex h-7 items-center">
                                <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleLogoPick} />
                                {logo ? (
                                    <div className="inline-flex h-7 items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 pl-1.5 pr-1 dark:border-zinc-700 dark:bg-zinc-800/60">
                                        <img src={logo} alt="" className="h-5 w-5 rounded object-contain" />
                                        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{t('questionsCreate.logo.label')}</span>
                                        <button type="button" onClick={() => setLogo('')} aria-label={t('common.delete')} className="rounded p-0.5 text-zinc-400 hover:text-red-500">
                                            <IconX size={12} />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => logoInputRef.current?.click()}
                                        disabled={uploadingLogo}
                                        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-2.5 text-xs font-medium text-zinc-500 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-400 dark:hover:bg-zinc-800"
                                    >
                                        {uploadingLogo ? <IconLoader2 size={13} className="animate-spin" /> : <IconPhotoPlus size={13} />}
                                        {t('questionsCreate.logo.upload')}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Font size */}
                        <div className={tbCol}>
                            <div className={tbHead}><span className={tbLabel}>{t('questionsCreate.fontSize')}</span></div>
                            <div className="flex h-7 items-center overflow-hidden rounded-md border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                                <Tip label={t('questionsCreate.fontDecrease')}>
                                    <button type="button" onClick={() => setFontScale((v) => Math.max(0.7, +(v - 0.1).toFixed(2)))} aria-label={t('questionsCreate.fontDecrease')} className="h-7 px-2 text-zinc-600 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800">
                                        <IconMinus size={13} />
                                    </button>
                                </Tip>
                                <span className="min-w-[42px] border-x border-zinc-200 px-1 text-center text-xs font-semibold text-zinc-700 dark:border-zinc-700 dark:text-zinc-200">{num(Math.round(fontScale * 100))}%</span>
                                <Tip label={t('questionsCreate.fontIncrease')}>
                                    <button type="button" onClick={() => setFontScale((v) => Math.min(1.6, +(v + 0.1).toFixed(2)))} aria-label={t('questionsCreate.fontIncrease')} className="h-7 px-2 text-zinc-600 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800">
                                        <IconPlus size={13} />
                                    </button>
                                </Tip>
                            </div>
                        </div>

                        {/* Page size */}
                        <div className={tbCol}>
                            <div className={tbHead}><span className={tbLabel}>{t('questionsCreate.pageSize')}</span></div>
                            <div className="flex h-7 items-center overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-700">
                                {[['a4', t('questionsCreate.pageA4')], ['a5', t('questionsCreate.pageA5')], ['joint', t('questionsCreate.pageJoint')]].map(([val, label]) => (
                                    <button
                                        key={val}
                                        type="button"
                                        onClick={() => setPageSize(val)}
                                        className={`h-7 px-3 text-xs font-medium transition-colors ${pageSize === val ? 'bg-primary text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Set number (enable/disable + value + shuffle) */}
                        <div className={tbCol}>
                            <div className={tbHead}>
                                <span className={tbLabel}>{t('questionsCreate.setNo')}</span>
                                <Toggle on={setNoEnabled} onClick={() => setSetNoEnabled((v) => !v)} label={t('questionsCreate.setNoHint')} />
                            </div>
                            <div className="flex h-7 items-center gap-1.5">
                                {setNoEnabled ? (
                                    <>
                                        <input
                                            type="text"
                                            value={setNo}
                                            onChange={(e) => setSetNo(e.target.value)}
                                            placeholder={t('questionsCreate.setNoPlaceholder')}
                                            className={`${tbInput} w-[70px]`}
                                        />
                                        <Tip label={t('questionsCreate.shuffleHint')}>
                                            <button
                                                type="button"
                                                onClick={shuffleQuestions}
                                                disabled={selectedList.length < 2}
                                                aria-label={t('questionsCreate.shuffle')}
                                                className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                            >
                                                <IconArrowsShuffle size={13} />
                                                {t('questionsCreate.shuffle')}
                                            </button>
                                        </Tip>
                                    </>
                                ) : (
                                    <span className="text-xs text-zinc-400 dark:text-zinc-500">{t('questionsCreate.off')}</span>
                                )}
                            </div>
                        </div>

                        {/* Layout options popover — spacing · MCQ columns · watermark */}
                        <div className={tbCol}>
                            <div className={tbHead}><span className={tbLabel}>{t('questionsCreate.layout.title')}</span></div>
                            <div className="flex h-7 items-center">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button type="button" className="inline-flex h-7 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800">
                                            <IconAdjustmentsHorizontal size={13} />
                                            {t('questionsCreate.layout.open')}
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="w-72 font-hind">
                                        <div className="space-y-3.5">
                                            {/* Question spacing */}
                                            <div>
                                                <p className={`${tbLabel} mb-1.5`}>{t('questionsCreate.layout.spacing')}</p>
                                                <div className="flex overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-700">
                                                    {[['compact', t('questionsCreate.layout.compact')], ['normal', t('questionsCreate.layout.normal')], ['relaxed', t('questionsCreate.layout.relaxed')]].map(([val, label]) => (
                                                        <button key={val} type="button" onClick={() => setQuestionGap(val)} className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${questionGap === val ? 'bg-primary text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'}`}>
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* MCQ columns */}
                                            <div>
                                                <p className={`${tbLabel} mb-1.5`}>{t('questionsCreate.layout.mcqColumns')}</p>
                                                <div className="flex overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-700">
                                                    {[[1, t('questionsCreate.layout.oneCol')], [2, t('questionsCreate.layout.twoCol')]].map(([val, label]) => (
                                                        <button key={val} type="button" onClick={() => setMcqColumns(val)} className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${mcqColumns === val ? 'bg-primary text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'}`}>
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* Watermark — text OR image (mutually exclusive), plus placement */}
                                            <div className="rounded-lg border border-zinc-200 p-2.5 dark:border-zinc-700">
                                                <p className={`${tbLabel} mb-1.5`}>{t('questionsCreate.layout.watermark')}</p>
                                                {/* Type tabs */}
                                                <div className="mb-2 flex overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-700">
                                                    {[['text', t('questionsCreate.layout.wmText')], ['image', t('questionsCreate.layout.wmImage')]].map(([val, label]) => (
                                                        <button key={val} type="button" onClick={() => setWatermarkType(val)} className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${watermarkType === val ? 'bg-primary text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'}`}>
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                                {/* Text mode */}
                                                {watermarkType === 'text' ? (
                                                    <input type="text" value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)} placeholder={t('questionsCreate.layout.watermarkPlaceholder')} className={`${tbInput} w-full`} />
                                                ) : (
                                                    /* Image mode */
                                                    <>
                                                        <input ref={watermarkImgRef} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleWatermarkPick} />
                                                        {watermarkImage ? (
                                                            <div className="flex items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 p-1.5 dark:border-zinc-700 dark:bg-zinc-800/60">
                                                                <img src={watermarkImage} alt="" className="h-6 w-6 rounded object-contain" />
                                                                <span className="flex-1 truncate text-xs font-medium text-zinc-600 dark:text-zinc-300">{t('questionsCreate.logo.label')}</span>
                                                                <button type="button" onClick={() => setWatermarkImage('')} aria-label={t('common.delete')} className="rounded p-0.5 text-zinc-400 hover:text-red-500">
                                                                    <IconX size={12} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button type="button" onClick={() => watermarkImgRef.current?.click()} disabled={uploadingWatermark} className="inline-flex h-7 w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-2.5 text-xs font-medium text-zinc-500 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-400 dark:hover:bg-zinc-800">
                                                                {uploadingWatermark ? <IconLoader2 size={13} className="animate-spin" /> : <IconPhotoPlus size={13} />}
                                                                {t('questionsCreate.layout.wmUpload')}
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                {/* Placement — 3×3 anchor grid */}
                                                <p className={`${tbLabel} mb-1.5 mt-2.5`}>{t('questionsCreate.layout.wmPosition')}</p>
                                                <div className="grid w-[76px] grid-cols-3 gap-1">
                                                    {WM_POS_KEYS.map((pos) => (
                                                        <button
                                                            key={pos}
                                                            type="button"
                                                            onClick={() => setWatermarkPos(pos)}
                                                            aria-label={pos}
                                                            className={`flex h-6 w-6 items-center justify-center rounded border transition-colors ${watermarkPos === pos ? 'border-primary bg-primary/10' : 'border-zinc-200 hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800'}`}
                                                        >
                                                            <span className={`h-1.5 w-1.5 rounded-full ${watermarkPos === pos ? 'bg-primary' : 'bg-zinc-300 dark:bg-zinc-600'}`} />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                      </div>

                      {/* Second row */}
                      <div className="mt-3 flex flex-wrap items-stretch gap-x-8 gap-y-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                        {/* Answer key mode — reveals the mark-correct controls */}
                        <div className={tbCol}>
                            <div className={tbHead}><span className={tbLabel}>{t('questionsCreate.answerMode')}</span></div>
                            <div className="flex h-7 items-center gap-2">
                                <Toggle on={answerMode} onClick={() => setAnswerMode((v) => !v)} label={t('questionsCreate.answerModeHint')} />
                                <span className="text-xs text-zinc-500 dark:text-zinc-400">{answerMode ? t('questionsCreate.on') : t('questionsCreate.off')}</span>
                            </div>
                        </div>

                        {/* Header note (instruction line under the paper header) */}
                        <div className={tbCol}>
                            <div className={tbHead}>
                                <span className={tbLabel}>{t('questionsCreate.headerNote')}</span>
                                <Toggle on={headerNoteEnabled} onClick={() => setHeaderNoteEnabled((v) => !v)} label={t('questionsCreate.paper.headerNoteHint')} />
                            </div>
                            <div className="flex h-7 items-center">
                                {headerNoteEnabled ? (
                                    <input
                                        type="text"
                                        value={headerNote}
                                        onChange={(e) => setHeaderNote(e.target.value)}
                                        placeholder={t('questionsCreate.paper.headerNotePlaceholder')}
                                        className={`${tbInput} w-[180px] max-w-[40vw]`}
                                    />
                                ) : (
                                    <span className="text-xs text-zinc-400 dark:text-zinc-500">{t('questionsCreate.off')}</span>
                                )}
                            </div>
                        </div>

                        {/* Footer text (end of paper) — last column, no divider */}
                        <div className={tbCol}>
                            <div className={tbHead}><span className={tbLabel}>{t('questionsCreate.footer')}</span></div>
                            <div className="flex h-7 items-center">
                                <input
                                    type="text"
                                    value={footerText}
                                    onChange={(e) => setFooterText(e.target.value)}
                                    placeholder={t('questionsCreate.paper.theEnd')}
                                    className={`${tbInput} w-[110px]`}
                                />
                            </div>
                        </div>
                      </div>
                    </div>

                    {loadingPaper ? previewSkeleton : preview}
                </div>
            </div>

            <LimitReachedModal opened={limitModalOpen} onClose={() => setLimitModalOpen(false)} />

            {/* Dislike → collect a reason (+ optional note) for the saved paper.
                strict = whitelist the note (letters/digits/space + । . , ? ! only) so no
                markup, script, URI scheme or special char can be entered or pasted. */}
            <AiTemplateFeedbackModal
                open={showFeedbackModal}
                onOpenChange={setShowFeedbackModal}
                submitting={feedbackLoading === 'negative'}
                reasons={QP_FEEDBACK_REASONS}
                strict
                onSubmit={(reason, note) => submitPaperFeedback('negative', reason, note)}
            />

            {/* Print preview — native rendering (math stays crisp); print charges 1 credit like export */}
            <Dialog open={printPreviewOpen} onOpenChange={setPrintPreviewOpen}>
                <DialogContent showClose={false} className="flex h-[90vh] max-w-4xl flex-col p-4 font-hind sm:p-5">
                    <button
                        type="button"
                        aria-label={t('common.cancel')}
                        onClick={() => setPrintPreviewOpen(false)}
                        className="absolute right-4 top-4 inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                    >
                        <IconX size={16} />
                    </button>
                    <DialogHeader className="mb-3 pr-10">
                        <DialogTitle>{t('questionsCreate.printPreview.title')}</DialogTitle>
                        <DialogDescription>{t('questionsCreate.printPreview.subtitle')}</DialogDescription>
                    </DialogHeader>
                    <div className="relative flex-1 overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800">
                        {printPreviewLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-100/70 dark:bg-zinc-800/70">
                                <IconLoader2 size={28} className="animate-spin text-primary" />
                            </div>
                        )}
                        <iframe
                            ref={attachPrintIframe}
                            title={t('questionsCreate.printPreview.title')}
                            className="h-full w-full border-0 bg-zinc-100"
                        />
                    </div>
                    <DialogFooter className="mt-4 gap-2 sm:gap-2">
                        <button
                            type="button"
                            onClick={() => setPrintPreviewOpen(false)}
                            className="rounded-lg border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmPrint}
                            disabled={printing || printPreviewLoading}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {printing ? <IconLoader2 size={16} className="animate-spin" /> : <IconPrinter size={16} />}
                            {t('questionsCreate.print')}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm before switching class/subject while questions are selected */}
            <Dialog open={!!pendingFilter} onOpenChange={(o) => { if (!o) cancelFilterChange(); }}>
                <DialogContent className="max-w-sm font-hind">
                    <DialogHeader>
                        <DialogTitle>{t('questionsCreate.resetConfirm.title')}</DialogTitle>
                        <DialogDescription>
                            {t('questionsCreate.resetConfirm.body', { count: num(selectedList.length) })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <button
                            type="button"
                            onClick={cancelFilterChange}
                            className="rounded-lg border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                        >
                            {t('questionsCreate.resetConfirm.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={confirmFilterChange}
                            className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600"
                        >
                            {t('questionsCreate.resetConfirm.confirm')}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </TooltipProvider>
    );
}
